import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircle, BookOpen, Brain, Calendar, CheckCircle2, ChevronDown, ClipboardList, Clock, Eye, HelpCircle, ShieldCheck, Target, TrendingUp, Users, Wrench, X } from 'lucide-react';
import { formatLastAnalysisDate } from '../utils/dateFormatter';
import { Bar, BarChart, CartesianGrid, Legend, PolarAngleAxis, PolarGrid, PolarRadiusAxis, Radar, RadarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useEffect, useMemo, useState } from 'react';
import { useLexLastSync } from '../contexts/LexLastSyncContext';

type TabId = 'esprit' | 'cadre' | 'maturite' | 'raci' | 'roadmap' | 'rituels';

interface GouvernanceSecuriteModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function InfoCard({
  title,
  icon,
  bullets,
}: {
  title: string;
  icon?: React.ReactNode;
  bullets: string[];
}) {
  return (
    <div className="bg-white/5 rounded-xl p-6 border border-emerald-500/20">
      <h4 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
        {icon ? <span className="text-emerald-300">{icon}</span> : null}
        <span>{title}</span>
      </h4>
      <ul className="space-y-2 text-sm text-gray-200 list-disc ml-5">
        {bullets.map((b) => (
          <li key={b}>{b}</li>
        ))}
      </ul>
    </div>
  );
}

function SectionTitle({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="bg-gradient-to-r from-emerald-600/20 via-teal-600/20 to-emerald-600/20 rounded-xl p-6 border border-emerald-500/30">
      <h3 className="text-2xl font-bold text-center text-white mb-2">{title}</h3>
      {subtitle ? <p className="text-gray-200 text-center text-sm">{subtitle}</p> : null}
    </div>
  );
}

type MaturityLevel = { niveau: number; label: string; description: string };
type PilierSecurite = {
  id: number;
  nom: string;
  nist?: string[]; // ex: ["Govern"]
  actuel?: number; // 0-5
  cible?: number; // 0-5
  focusImmediat?: string;
  avatars?: { name: string; src: string }[];
  intention: string;
  pourquoiCritique: string;
  concretementBZ: string;
  erreursFrequentes: string[];
  niveaux: MaturityLevel[];
  aFaireMaintenant: string[];
  preuvesVisibles: string[];
};

// ============================================================================
// DONNÉES — PILIERS (CADRE HOLISTIQUE) COMITÉ SÉCURITÉ (CYBER + OPS TI / PARC)
// ============================================================================
const piliersSecurite: PilierSecurite[] = [
  {
    id: 1,
    nom: 'Gouvernance & décisions (Comité)',
    nist: ['Govern'],
    actuel: 2,
    cible: 3,
    focusImmediat:
      'AS-IS: comité structuré. Cible: décisions et actions traçables de façon systématique.\n' +
      'Priorité: registre des décisions + registre des actions SMART + communication régulière.\n' +
      'Inclure systématiquement l’impact budgétaire: définir des enveloppes, arbitrer, suivre, et respecter.',
    avatars: [
      { name: 'François Delisle (A)', src: '/FP.png' },
      { name: 'Vincent Bourassa', src: '/VB.png' },
    ],
    intention:
      'Décider vite et juste, avec traçabilité: transformer des enjeux (cyber + opérations TI) en décisions claires, en actions suivies, et en communications applicables.',
    pourquoiCritique:
      'Sans gouvernance, la sécurité et les opérations TI deviennent réactives, incohérentes et dépendantes des individus. On perd du temps, on augmente le risque, et on s’épuise.',
    concretementBZ:
      'Cadence mensuelle, ordre du jour limité, décisions documentées (autorisé/refusé/sous conditions), registre des actions, communications régulières, et “Gate (point de contrôle)” pour éviter le flou. ' +
      'Inclure un volet budget: enveloppes (cyber / parc / outils), estimation coût (setup + mensuel), seuils d’approbation, et revue de suivi (budget vs réel).',
    erreursFrequentes: [
      'Gouvernance perçue comme bureaucratie (au lieu d’un accélérateur).',
      'Décisions non documentées et/ou sans preuves (permissions, clauses, logs, captures).',
      'Décisions sans estimation de coût/impact budget (on découvre la facture après).',
      'Exceptions qui deviennent permanentes.',
      'Mélanger les portées (BZ interne, clients, veille) dans un même point.',
    ],
    niveaux: [
      { niveau: 0, label: 'Ad-hoc', description: 'Décisions au feeling, pas de cadence, pas de trace.' },
      { niveau: 1, label: 'Informel', description: 'Quelques règles, mais décisions/ actions pas systématiquement suivies.' },
      { niveau: 2, label: 'Comité structuré', description: 'Cadence + ODJ + rôles de base; suivi partiel des actions.' },
      { niveau: 3, label: 'Décisions traçables', description: 'Registre décisions + actions SMART; communications régulières.' },
      { niveau: 4, label: 'Points de contrôle + preuves', description: 'Gates (points de contrôle) appliqués; preuves exigées; révision planifiée.' },
      { niveau: 5, label: 'Gouvernance réflexe', description: 'Décision/exécution fluides; la gouvernance devient un réflexe d’équipe.' },
    ],
    aFaireMaintenant: [
      'Finaliser la modal “Gouvernance — Comité TI et Cybersécurité” avec François (FD) et Vincent (VB) — contenu + cadrage final.',
      'Présenter la gouvernance à la prochaine réunion du comité (valider le cadre, les piliers, et l’approche maturité).',
      'Bâtir la gestion des réunions selon le standard PRIsME Master (s’inspirer de “Gestion des réunions” dans le Comité PRIsME).',
    ],
    preuvesVisibles: ['Agenda type', 'Registre décisions', 'Registre actions SMART', 'Enveloppes budget + suivi (budget vs réel)', 'Projet annuel + phases + billets'],
  },
  {
    id: 2,
    nom: 'IA responsable & cadre (NIST AI RMF)',
    nist: ['AI RMF', 'Govern'],
    actuel: 1.5,
    cible: 3,
    focusImmediat:
      'AS-IS: on adopte l’IA, mais les règles et preuves ne sont pas encore “par défaut”.\n' +
      'Cible: un cadre IA RMF appliqué systématiquement (cas d’usage + données + risques + contrôles + preuve), avec un “Gate” d’approbation.',
    avatars: [
      { name: 'François Delisle (A)', src: '/FP.png' },
      { name: 'Vincent Bourassa', src: '/VB.png' },
      { name: 'Guillaume Cloutier', src: '/GC.png' },
    ],
    intention:
      'Entrer dans l’IA “à pied joint” mais de façon responsable: accélérer l’exécution chez BZ tout en contrôlant les risques (données, conformité, sécurité, qualité), en s’appuyant sur le cadre NIST AI RMF.',
    pourquoiCritique:
      'Le maître, c’est l’humain: l’IA doit rester un conseiller, pas un décideur. Elle apporte un gain énorme, mais augmente aussi le risque (données sensibles, conformité Loi 25, fuite IP, décisions biaisées/hallucinations). Sans cadre, on crée du Shadow‑AI, on perd le contrôle, et on fragilise la confiance (interne et client).',
    concretementBZ:
      'Définir les règles d’usage (ce qui est permis/interdit), les outils autorisés, et les cas d’usage priorisés. ' +
      'Règle d’or (RACI): l’humain est (A/R) — l’IA est (C) pour la compétence et l’accélération; validation humaine obligatoire. ' +
      'Appliquer un “Gate (point de contrôle)” pour les outils IA et les cas d’usage à risque: données utilisées, flux, preuves fournisseur (DPA/SOC2/localisation), risques, contrôles, et décision. ' +
      'Former l’équipe (bonnes pratiques, confidentialité, validation humaine), et maintenir un registre IA (outils + cas d’usage + niveau de risque + propriétaire + date de révision).',
    erreursFrequentes: [
      'Utiliser l’IA avec des données sensibles sans règle claire (ou sans preuve).',
      'Mélanger “productivité” et “décision” (laisser l’IA décider sans validation humaine).',
      'Multiplier les outils (Shadow-AI) sans registre ni approbation.',
      'Absence d’évaluation structurée (risques, contrôles, preuves fournisseur).',
      'Pas de suivi: décisions IA non revues, exceptions qui durent.',
    ],
    niveaux: [
      { niveau: 0, label: 'Chaotique', description: 'Usage IA non encadré; outils multiples; aucune règle ni preuve.' },
      { niveau: 1, label: 'Expérimentation', description: 'Pilotes ponctuels; règles implicites; dépend des individus.' },
      { niveau: 2, label: 'Cadrage initial (AI RMF)', description: 'Politique brouillon + liste outils; évaluation partielle des risques.' },
      { niveau: 3, label: 'Gouverné', description: 'Registre IA + Gate d’approbation; validation humaine; communications/formation.' },
      { niveau: 4, label: 'Contrôlé & prouvable', description: 'Preuves fournisseur, revues planifiées, contrôles techniques, mesures et audits.' },
      { niveau: 5, label: 'Maîtrisé & évolutif', description: 'Cadre vivant (KPIs), amélioration continue, décisions rapides et cohérentes; référence interne.' },
    ],
    aFaireMaintenant: [
      'Créer le “registre IA” (outils + cas d’usage + données + risque + owner + conditions + date de révision).',
      'Définir le Gate IA (autorisé / refusé / autorisé sous conditions) et le Decision Packet 1 page (preuves minimales).',
      'Formaliser les règles: données interdites, données permises, validation humaine, et standards “prompt / sortie”.',
      'Piloter 2–3 cas d’usage BZ à forte valeur avec preuves (productivité + qualité), puis communiquer le standard à toute l’équipe.',
    ],
    preuvesVisibles: [
      'Politique IA (permis/interdit)',
      'Registre IA (outils + cas d’usage + owners)',
      'Évaluations AI RMF / Decision Packets',
      'Preuves fournisseur (DPA/SOC2/localisation)',
      'Formation + communications (standards IA)',
    ],
  },
  {
    id: 3,
    nom: 'Parc informatique BZ (infrastructure et postes)',
    nist: ['Identify', 'Protect'],
    actuel: 1.5,
    cible: 2.5,
    focusImmediat:
      'AS-IS: standard partiel et inégal sur le parc BZ (infra + postes), avec exceptions non maîtrisées, inventaire incomplet et variations de configuration.\n' +
      'Cible: baseline définie pour l’infrastructure et les postes, appliquée de façon systématique sur la majorité du parc, avec inventaire fiable, exceptions gérées et cycle de vie maîtrisé.',
    avatars: [
      { name: 'Vincent Bourassa (A)', src: '/VB.png' },
      { name: 'SAC — équipe Jean-Pierre (R)', src: '/BZ.png' },
    ],
    intention:
      'Garantir un parc informatique BZ solide, standardisé et maintenable — autant pour l’infrastructure que pour les postes — afin de soutenir l’exécution et réduire l’instabilité.',
    pourquoiCritique:
      'Un parc hétérogène crée des incidents, de la friction, et rend le support coûteux. La posture sécurité s’effondre si la base (infrastructure + postes) n’est pas stable.',
    concretementBZ:
      'Baseline d’infrastructure et de postes (config, hardening, apps autorisées, standards réseau/équipement), cycle de vie, inventaire fiable, et suivi de conformité.',
    erreursFrequentes: [
      'Tolérer trop d’exceptions “temporaires”.',
      'Pas de baseline clairement communiquée pour l’infra et les postes.',
      'Inventaire incomplet (on ne sait pas ce qu’on opère réellement).',
      'Cycle de vie non maîtrisé (équipements, retours, effacements, remplacements).',
    ],
    niveaux: [
      { niveau: 0, label: 'Non standard', description: 'Chaque poste est différent; support au cas par cas.' },
      { niveau: 1, label: 'Standard partiel', description: 'Quelques règles, mais pas de baseline complète.' },
      { niveau: 2, label: 'Baseline définie', description: 'Baseline documentée; adoption variable.' },
      { niveau: 3, label: 'Baseline appliquée', description: 'Provisioning aligné; exceptions suivies.' },
      { niveau: 4, label: 'Conformité mesurée', description: 'KPIs de conformité; écarts priorisés.' },
      { niveau: 5, label: 'Parc optimisé', description: 'Amélioration continue; instabilité rare; support prévisible.' },
    ],
    aFaireMaintenant: [
      'Objectif 1.5 → 2.5: passer d’un standard “partiel” à une baseline définie + appliquée sur la majorité du parc BZ (infra + postes), avec exceptions gérées.',
      'Définir la baseline cible pour les postes et l’infrastructure (liste “autorisé/standard/interdit”).',
      'Créer un inventaire “source de vérité” du parc BZ.',
      'Définir le cycle de vie (entrée/sortie/remplacement) + effacement sécurisé.',
    ],
    preuvesVisibles: ['Baseline parc BZ', 'Inventaire', 'Procédure onboarding/offboarding', 'Standards infrastructure'],
  },
  {
    id: 4,
    nom: 'Identité & accès',
    nist: ['Protect'],
    actuel: 3,
    cible: 4,
    focusImmediat:
      'AS-IS: accès gouvernés (revues, règles d’exception, traçabilité). Cible: authentification moderne.\n' +
      'Priorité: Passkeys/FIDO2 + réduction du phishing + revues d’accès systématiques + exceptions temporaires avec date de révision.',
    avatars: [
      { name: 'François Delisle', src: '/FP.png' },
      { name: 'Vincent Bourassa (A)', src: '/VB.png' },
    ],
    intention:
      'Réduire le risque d’accès non autorisé: MFA, passkeys/FIDO2, comptes admin maîtrisés, et revues d’accès.',
    pourquoiCritique:
      'L’identité est la première surface d’attaque. Une posture TI solide commence par des accès maîtrisés, simples et systématiques.',
    concretementBZ:
      'MFA obligatoire, tests Passkey/FIDO2, séparation des privilèges, revues périodiques, et règles claires pour les exceptions.',
    erreursFrequentes: [
      'Comptes admin utilisés au quotidien.',
      'Exceptions MFA non suivies.',
      'Accès accordés sans date de révision.',
      'Manque de revue d’accès (on accumule).',
    ],
    niveaux: [
      { niveau: 0, label: 'Faible', description: 'MFA non systématique; privilèges non contrôlés.' },
      { niveau: 1, label: 'MFA partiel', description: 'MFA pour certains; exceptions nombreuses.' },
      { niveau: 2, label: 'MFA standard', description: 'MFA généralisé; contrôles admin en place.' },
      { niveau: 3, label: 'Accès gouvernés', description: 'Revues d’accès; règles d’exception; traçabilité.' },
      { niveau: 4, label: 'Authentification moderne', description: 'Passkeys/FIDO2; réduction du phishing; posture robuste.' },
      { niveau: 5, label: 'Identité exemplaire', description: 'Accès minimaux, revus; incidents d’identité rares.' },
    ],
    aFaireMaintenant: ['Formaliser comptes admin (séparation, usage).', 'Plan de déploiement Passkey/FIDO2 (pilote → généralisation).', 'Règle: tout accès exceptionnel a une date de révision.'],
    preuvesVisibles: ['Politique MFA', 'Registre exceptions', 'Rapport revue d’accès'],
  },
  {
    id: 5,
    nom: 'Vulnérabilités & patching',
    nist: ['Identify', 'Protect'],
    actuel: 1.5,
    cible: 2.5,
    focusImmediat:
      'AS-IS: patching irrégulier / partiel. Cible: cadence et suivi structurés.\n' +
      'Priorité: cadence 30/60/90 + mesure Action1 (couverture/backlog) + registre d’exceptions avec date de révision + priorisation basée sur le risque (pas le bruit).',
    avatars: [
      { name: 'Vincent Bourassa (A)', src: '/VB.png' },
      { name: 'Mikael Drouin Bouchard', src: '/MDB.png' },
    ],
    intention:
      'Réduire l’exposition: détecter, prioriser et corriger les vulnérabilités (poste, serveurs, outils), avec exceptions gérées.',
    pourquoiCritique:
      'Sans cadence de patching, les vulnérabilités s’accumulent et un incident devient une question de temps.',
    concretementBZ:
      'Cadence patch (30/60/90), Action1 pour mesurer et exécuter, exceptions documentées, et priorisation basée sur risque.',
    erreursFrequentes: [
      'Patching “quand on a le temps”.',
      'Pas de mesure de couverture.',
      'Exceptions sans échéance.',
      'Priorisation basée sur bruit, pas sur risque.',
    ],
    niveaux: [
      { niveau: 0, label: 'Réactif', description: 'On patch après un problème.' },
      { niveau: 1, label: 'Cadence irrégulière', description: 'Quelques cycles, couverture inconnue.' },
      { niveau: 2, label: 'Cadence définie', description: 'Cycles documentés, exécution partielle.' },
      { niveau: 3, label: 'Couverture mesurée', description: 'KPIs de conformité; exceptions suivies.' },
      { niveau: 4, label: 'Priorisation risque', description: 'Remédiation selon criticité; délais cibles.' },
      { niveau: 5, label: 'Hygiène exemplaire', description: 'Faible backlog; exceptions rares et temporaires.' },
    ],
    aFaireMaintenant: ['Définir SLA patch (critique/haut/moyen).', 'Mettre une vue conformité (Action1).', 'Créer un registre exceptions vulnérabilités (raison/date).'],
    preuvesVisibles: ['Rapport conformité patch', 'Registre exceptions', 'SLA patch'],
  },
  {
    id: 6,
    nom: 'Données & conformité (Loi 25)',
    nist: ['Govern', 'Protect'],
    actuel: 2,
    cible: 3,
    focusImmediat:
      'AS-IS: EFVP appliquée quand requis, mais contrôles/preuves variables. Cible: contrôles opérationnels.\n' +
      'Priorité: inventaire des données sensibles + règles de rétention + accès/partage contrôlés + exceptions tracées + preuves prêtes (audit/incident).',
    avatars: [
      { name: 'François Delisle (A)', src: '/FP.png' },
      { name: 'Mikael Drouin Bouchard (R)', src: '/MDB.png' },
    ],
    intention:
      'Protéger les données et respecter les obligations (Loi 25) au-delà des outils: EFVP quand requis, gouvernance des données, contrôles d’accès, rétention, preuves, et “privacy by design”.',
    pourquoiCritique:
      'Un incident vie privée ou une non-conformité détruit la confiance, mais expose aussi BZ à des obligations légales (déclarations, enquêtes/inspections, exigences de preuve) et à des pénalités/amendes potentielles. La conformité doit être opérationnelle, pas théorique.',
    concretementBZ:
      'EFVP quand requis (outils/cas à risque) + décision documentée. ' +
      'Inventaire simple des données sensibles: où elles vivent, qui y accède, et règles de rétention. ' +
      'Contrôles appliqués: accès (moindre privilège), partage, chiffrement/DLP au besoin, et exceptions gérées. ' +
      'Fournisseurs: diligence + preuves réutilisables (audit/incident).',
    erreursFrequentes: [
      'Faire l’EFVP après la décision.',
      'Pas d’inventaire des données confidentielles et sensibles (on ne sait pas exactement où elles vivent, qui y accède, et combien de temps).',
      'Manquer de preuves/traçabilité (on fait les bonnes choses, mais on ne peut pas le démontrer rapidement en audit/incident).',
      'Pas de classification / rétention claire.',
    ],
    niveaux: [
      { niveau: 0, label: 'Non structuré', description: 'Pas de cadre; décisions au cas par cas.' },
      { niveau: 1, label: 'Réactif', description: 'On documente quand ça chauffe.' },
      { niveau: 2, label: 'EFVP appliquée', description: 'EFVP quand requis; documentation variable.' },
      { niveau: 3, label: 'Contrôles opérationnels', description: 'Règles + contrôles appliqués (accès, partage, DLP/chiffrement, rétention); traçabilité et exceptions gérées.' },
      { niveau: 4, label: 'Preuves solides', description: 'Diligence + preuves réutilisables; révisions planifiées.' },
      { niveau: 5, label: 'Référence', description: 'Process maîtrisé; décisions défendables; confiance élevée.' },
    ],
    aFaireMaintenant: ['Lister les cas “EFVP obligatoire”.', 'Structurer les preuves types (permissions, DPA, SOC2, localisation).', 'Définir la classification/rétention cible (M365).'],
    preuvesVisibles: ['EFVP versionnées', 'Liste cas EFVP', 'Politiques rétention/classification', 'DPA + preuves fournisseur', 'Règles DLP/partage (si applicable)'],
  },
  {
    id: 7,
    nom: 'Détection & surveillance',
    nist: ['Detect'],
    actuel: 3,
    cible: 4,
    focusImmediat:
      'AS-IS: surveillance active (revues, KPIs, owner des alertes). Cible: détection efficace.\n' +
      'Priorité: réduire le bruit, renforcer les playbooks, et rendre la réponse “prête” (alertes actionnables + seuils + escalade + preuves).',
    avatars: [
      { name: 'Vincent Bourassa (A)', src: '/VB.png' },
      { name: 'SAC — équipe Jean-Pierre (R)', src: '/BZ.png' },
    ],
    intention:
      'Voir ce qui se passe: journaux, alerting, monitoring et indicateurs — pour détecter plus tôt et agir plus vite.',
    pourquoiCritique:
      'Sans détection, on découvre les problèmes trop tard. La rapidité de détection réduit l’impact.',
    concretementBZ:
      'Logs centralisés quand pertinent, alertes sur événements clés, revues régulières, et KPIs simples (temps détection, incidents).',
    erreursFrequentes: [
      'Trop d’alertes (bruit) ou pas assez (aveugle).',
      'Pas de propriétaire des alertes.',
      'Absence de tableau de bord.',
      'Logs non conservés ou non exploitables.',
    ],
    niveaux: [
      { niveau: 0, label: 'Aveugle', description: 'Peu/pas de logs utiles; pas d’alertes.' },
      { niveau: 1, label: 'Minimal', description: 'Quelques logs; réaction manuelle.' },
      { niveau: 2, label: 'Alertes de base', description: 'Alertes sur événements majeurs; suivi partiel.' },
      { niveau: 3, label: 'Surveillance active', description: 'Revues, KPIs, propriétaire des alertes.' },
      { niveau: 4, label: 'Détection efficace', description: 'Bruit maîtrisé; détection rapide; playbooks.' },
      { niveau: 5, label: 'Anticipation', description: 'Tendances, prévention, amélioration continue.' },
    ],
    aFaireMaintenant: ['Définir 10 alertes “must have”.', 'Nommer un owner de surveillance.', 'Créer un mini dashboard (KPI détection/réponse).'],
    preuvesVisibles: ['Liste alertes', 'Dashboard', 'Journal revues mensuelles'],
  },
  {
    id: 8,
    nom: 'Réponse & reprise',
    nist: ['Respond', 'Recover'],
    actuel: 1.5,
    cible: 2.5,
    focusImmediat:
      'AS-IS: procédures écrites, mais pas encore “réflexe” ni testées systématiquement. Cible: rôles + exécution plus prévisible.\n' +
      'Priorité: clarifier les rôles, tester par un tabletop, standardiser post-mortem, et améliorer la gestion des preuves + communications.',
    avatars: [
      { name: 'François Delisle (A)', src: '/FP.png' },
      { name: 'FM (R)', src: '/FM.png' },
      { name: 'JMC (R)', src: '/JMC.png' },
    ],
    intention:
      'Être prêt: procédures, rôles, exercices et post-mortem — pour réduire l’impact et apprendre plus vite.',
    pourquoiCritique:
      'L’incident arrivera tôt ou tard. La différence entre “dommage” et “désastre” est la préparation.',
    concretementBZ:
      'Procédures incidents (clients + interne), tabletop, gestion des preuves, communication, et amélioration continue post-incident.',
    erreursFrequentes: [
      'Procédures non testées.',
      'Rôles flous pendant l’incident.',
      'Pas de post-mortem (on répète les mêmes erreurs).',
      'Preuves mal gérées.',
    ],
    niveaux: [
      { niveau: 0, label: 'Improvisé', description: 'Réponse ad-hoc, stress, pertes.' },
      { niveau: 1, label: 'Procédures écrites', description: 'Docs existent mais pas testés.' },
      { niveau: 2, label: 'Rôles définis', description: 'Rôles connus; exécution variable.' },
      { niveau: 3, label: 'Exercices', description: 'Tabletop; améliorations appliquées.' },
      { niveau: 4, label: 'Réponse robuste', description: 'Playbooks; communication maîtrisée; preuves OK.' },
      { niveau: 5, label: 'Organisation résiliente', description: 'Apprentissage rapide; impact réduit; confiance élevée.' },
    ],
    aFaireMaintenant: ['Mettre à jour la procédure incident client (PD).', 'Planifier 1 exercice tabletop/trimestre.', 'Standardiser post-mortem (leçons apprises).'],
    preuvesVisibles: ['Procédure IR', 'Rapport tabletop', 'Post-mortem'],
  },
  {
    id: 9,
    nom: 'Veille technologique',
    nist: ['Protect', 'Govern'],
    actuel: 3,
    cible: 3.5,
    focusImmediat:
      'AS-IS: roadmap en place (choix reliés à un plan + suivi). Cible: architecture plus cohérente.\n' +
      'Priorité: intégrer les composants (SSO/MFA, logs, réseau, sauvegardes), réduire les doublons (“1 standard par catégorie”), et planifier les décommissions pour maîtriser la complexité.',
    avatars: [
      { name: 'François Delisle (A)', src: '/FP.png' },
      { name: 'Vincent Bourassa (R)', src: '/VB.png' },
      { name: 'Dany Bourassa (R)', src: '/DB.png' },
      { name: 'ED (R)', src: '/ED.png' },
      { name: 'RD (R)', src: '/RD.png' },
    ],
    intention:
      'Piloter les choix d’architecture BZ au bon niveau: infra cloud et on‑premise, SaaS/IaaS, identité, réseau, données et résilience. Garder une cohérence globale, réduire la complexité, et intégrer les composants (plutôt que multiplier les outils).',
    pourquoiCritique:
      'Les mauvais choix techno créent des coûts, de la complexité, et de l’instabilité. La veille doit nourrir des décisions, pas du bruit.',
    concretementBZ:
      'Sujets testés/évalués en comité, décisions sous conditions, et intégration progressive (ex: VLAN mgmt, backups, Purview, SASE).',
    erreursFrequentes: [
      'Tester sans décider.',
      'Multiplier les outils sans standard.',
      'Sauter la preuve (sécurité/contrats/permissions).',
      'Veille non reliée à un plan d’action.',
    ],
    niveaux: [
      { niveau: 0, label: 'Éparpillé', description: 'Choix non alignés; trop d’outils.' },
      { niveau: 1, label: 'Occasionnel', description: 'Tests ponctuels; peu de standard.' },
      { niveau: 2, label: 'Choix encadrés', description: 'Décisions en comité; quelques standards.' },
      { niveau: 3, label: 'Roadmap', description: 'Choix reliés à une roadmap; suivi.' },
      { niveau: 4, label: 'Architecture cohérente', description: 'Composants intégrés; complexité maîtrisée.' },
      { niveau: 5, label: 'Référence', description: 'Évolution continue; décisions rapides; qualité stable.' },
    ],
    aFaireMaintenant: ['Lister les 5 sujets de veille prioritaires.', 'Formaliser “test → décision → déploiement”.', 'Documenter les décisions SASE/SSE, VLAN mgmt, backups.'],
    preuvesVisibles: ['Roadmap techno', 'Décisions outillage', 'Dossiers de tests'],
  },
];

function CadreHolistiqueTab() {
  const [expandedPilier, setExpandedPilier] = useState<number | null>(null);
  const [maturityGuidePilierId, setMaturityGuidePilierId] = useState<number | null>(null);
  const [isPatchingGuideOpen, setIsPatchingGuideOpen] = useState(false);

  const maturityGuidePilier = useMemo(() => {
    if (maturityGuidePilierId == null) return null;
    return piliersSecurite.find((p) => p.id === maturityGuidePilierId) ?? null;
  }, [maturityGuidePilierId]);

  return (
    <div className="space-y-6">
      <SectionTitle
        title="Cadre holistique — Piliers de maturité (0–5)"
        subtitle="9 piliers simples pour piloter cyber + opérations TI/parc. Chaque pilier se mesure de 0 à 5 et se prouve."
      />

      <div className="space-y-4">
        {piliersSecurite.map((p) => (
          <div key={p.id} className="bg-white/5 rounded-xl border border-emerald-500/20 overflow-hidden">
            <button
              onClick={() => setExpandedPilier(expandedPilier === p.id ? null : p.id)}
              className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white font-bold text-sm">
                  {p.id}
                </div>
                <div className="text-left">
                  <div className="flex items-center flex-wrap gap-2">
                    <h4 className="text-lg font-bold text-white">{p.nom}</h4>
                    {p.nist && p.nist.length > 0 && (
                      <div className="flex items-center gap-1.5">
                        {p.nist.map((t) => (
                          <span key={t} className="px-2 py-0.5 rounded-full text-[11px] bg-emerald-500/15 border border-emerald-500/30 text-emerald-200">
                            NIST: {t}
                          </span>
                        ))}
                      </div>
                    )}
                    {typeof p.actuel === 'number' && typeof p.cible === 'number' && (
                      <span className="px-2 py-0.5 rounded-full text-[11px] bg-white/10 border border-white/15 text-gray-100">
                        AS-IS {p.actuel} → Cible {p.cible}
                      </span>
                    )}
                  </div>
                  {p.avatars && p.avatars.length > 0 && (
                    <div className="flex items-center gap-1.5 mt-1">
                      {[...p.avatars]
                        .sort((a, b) => {
                          const aIsA = a.name.includes('(A)') ? 1 : 0;
                          const bIsA = b.name.includes('(A)') ? 1 : 0;
                          return bIsA - aIsA; // A first
                        })
                        .map((a) => (
                        <img
                          key={a.name}
                          src={a.src}
                          alt={a.name}
                          className={
                            `rounded-full object-cover bg-white/10 ` +
                            (a.name.includes('(A)')
                              ? 'w-6 h-6 border-2 border-amber-300 shadow-sm shadow-amber-300/20'
                              : 'w-5 h-5 border border-emerald-400')
                          }
                          title={a.name}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className={`transform transition-transform ${expandedPilier === p.id ? 'rotate-180' : ''}`}>
                <ChevronDown className="w-5 h-5 text-gray-300" />
              </div>
            </button>

            {expandedPilier === p.id && (
              <div className="p-6 border-t border-emerald-500/20 space-y-4">
                {/* Intention */}
                <div>
                  <h5 className="text-sm font-bold text-emerald-300 mb-2 flex items-center gap-2">
                    <Target className="w-4 h-4" />
                    Intention
                    {p.id === 9 ? (
                      <span className="ml-1 inline-flex items-center rounded-full border border-fuchsia-300/40 bg-fuchsia-500/10 px-2 py-0.5 text-[10px] font-extrabold tracking-[0.22em] text-fuchsia-200">
                        BETA
                      </span>
                    ) : null}
                  </h5>
                  <p className="text-gray-200 text-sm leading-relaxed">{p.intention}</p>
                </div>

                {/* AS-IS -> cible */}
                {typeof p.actuel === 'number' && typeof p.cible === 'number' && p.cible > p.actuel && (
                  <div className="bg-emerald-500/10 rounded-xl border border-emerald-500/20 p-4">
                    <h5 className="text-sm font-bold text-emerald-200 mb-2 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" />
                      Focus immédiat: passer de {p.actuel} à {p.cible}
                    </h5>
                    {p.focusImmediat ? (
                      <p className="text-gray-200 text-sm leading-relaxed whitespace-pre-line">{p.focusImmediat}</p>
                    ) : (
                      <p className="text-gray-200 text-sm leading-relaxed">
                        Clarifier le “AS-IS” (ce qui est vrai aujourd’hui), définir la “Cible” (ce qui doit être vrai), puis rendre ça observable avec des preuves.
                      </p>
                    )}
                  </div>
                )}

                {/* Pourquoi critique */}
                <div>
                  <h5 className="text-sm font-bold text-yellow-300 mb-2 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Pourquoi c’est critique
                  </h5>
                  <p className="text-gray-200 text-sm leading-relaxed">{p.pourquoiCritique}</p>
                </div>

                {/* Concrètement BZ */}
                <div>
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <h5 className="text-sm font-bold text-emerald-300 flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4" />
                      Concrètement chez BZ
                    </h5>
                    {p.id === 5 ? (
                      <button
                        type="button"
                        onClick={() => setIsPatchingGuideOpen(true)}
                        className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2 py-1 text-[11px] text-emerald-100 border border-emerald-300/30 hover:bg-white/15 transition-colors"
                        title="Guide patching (30/60/90)"
                      >
                        <BookOpen className="w-3.5 h-3.5" />
                        Guide
                      </button>
                    ) : null}
                  </div>
                  <p className="text-gray-200 text-sm leading-relaxed">{p.concretementBZ}</p>
                </div>

                {/* Erreurs fréquentes */}
                <div>
                  <h5 className="text-sm font-bold text-red-300 mb-2 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Erreurs fréquentes
                  </h5>
                  <ul className="space-y-2">
                    {p.erreursFrequentes.map((e, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-gray-200 text-sm">
                        <X className="w-4 h-4 text-red-300 flex-shrink-0 mt-0.5" />
                        <span>{e}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Niveaux 0 → 5 */}
                <div>
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <h5 className="text-sm font-bold text-teal-200 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" />
                      Niveaux 0 → 5
                    </h5>
                    <button
                      type="button"
                      onClick={() => setMaturityGuidePilierId(p.id)}
                      className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2 py-1 text-[11px] text-emerald-100 border border-emerald-300/30 hover:bg-white/15 transition-colors"
                      title="Guide des niveaux 0–5"
                    >
                      <HelpCircle className="w-3.5 h-3.5" />
                      Guide niveaux
                    </button>
                  </div>
                  <div className="space-y-2">
                    {p.niveaux.map((n) => (
                      <div key={n.niveau} className="bg-slate-900/40 rounded-lg p-3 border border-emerald-500/15">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-bold text-emerald-300">{n.niveau}</span>
                          <span className="text-xs text-gray-500">•</span>
                          <span className="text-xs font-semibold text-teal-200">{n.label}</span>
                        </div>
                        <p className="text-gray-200 text-xs leading-relaxed">{n.description}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* À faire maintenant */}
                {p.aFaireMaintenant && p.aFaireMaintenant.length > 0 && (
                  <div>
                    <h5 className="text-sm font-bold text-blue-200 mb-2 flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4" />
                      À faire maintenant (N+1)
                    </h5>
                    <ul className="space-y-2">
                      {p.aFaireMaintenant.map((a, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-gray-200 text-sm">
                          <CheckCircle2 className="w-4 h-4 text-blue-200 flex-shrink-0 mt-0.5" />
                          <span>{a}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Preuves visibles */}
                {p.preuvesVisibles && p.preuvesVisibles.length > 0 && (
                  <div>
                    <h5 className="text-sm font-bold text-emerald-300 mb-2 flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4" />
                      Preuves visibles
                    </h5>
                    <div className="flex flex-wrap gap-2">
                      {p.preuvesVisibles.map((pr, idx) => (
                        <span key={idx} className="px-2 py-1 bg-emerald-500/15 text-emerald-200 text-xs rounded border border-emerald-500/25">
                          {pr}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="bg-gradient-to-r from-emerald-600/15 via-teal-600/15 to-emerald-600/15 rounded-xl p-6 border border-emerald-500/25">
        <div className="text-center">
          <p className="text-lg font-semibold text-white leading-relaxed italic">
            La maturité ne s’improvise pas: elle se décide, s’exécute et se prouve.
          </p>
        </div>
      </div>

      {/* Guide niveaux 0-5 */}
      <AnimatePresence>
        {maturityGuidePilier && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMaturityGuidePilierId(null)}
              className="fixed inset-0 bg-black/70 backdrop-blur-sm z-60"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.98, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.98, y: 10 }}
              className="fixed inset-0 z-61 flex items-center justify-center p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-full max-w-3xl rounded-2xl border border-emerald-500/30 bg-slate-950/90 shadow-2xl shadow-emerald-500/10">
                <div className="flex items-start justify-between gap-3 p-4 border-b border-emerald-500/20">
                  <div>
                    <p className="text-lg font-bold text-white">Guide — niveaux 0 à 5</p>
                    <p className="text-sm text-gray-200 mt-1">
                      Pilier: <strong className="text-white">{maturityGuidePilier.nom}</strong>. Chaque niveau = un état observable, avec des preuves.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setMaturityGuidePilierId(null)}
                    className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                    aria-label="Fermer"
                  >
                    <X className="w-5 h-5 text-gray-300" />
                  </button>
                </div>

                <div className="p-3 sm:p-4 space-y-4 text-sm text-gray-200 max-h-[75vh] overflow-y-auto break-words">
                  {maturityGuidePilier.id === 1 ? (
                    <>
                      <div className="rounded-xl bg-white/5 border border-emerald-500/15 p-4">
                        <p className="font-semibold text-white mb-2">Lecture holistique (Gouvernance)</p>
                        <ul className="list-disc ml-5 space-y-1.5">
                          <li>
                            <strong className="text-white">Jalon 1.5 (entre 1 et 2)</strong> = comité structuré (cadence, ODJ, rôles) mais la traçabilité n’est pas systématique.
                          </li>
                          <li>
                            <strong className="text-white">Jalon 2.5 (entre 2 et 3)</strong> = décisions et actions traçables de façon systématique
                            (registre des décisions + registre des actions SMART + communication régulière).
                          </li>
                          <li><strong className="text-white">Niveau 4</strong> = “points de contrôle” + preuves exigées + dates de révision (décisions défendables).</li>
                        </ul>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                          <div className="rounded-lg bg-slate-900/40 border border-emerald-500/15 p-3">
                            <p className="text-xs font-bold text-emerald-200 mb-1">Humain</p>
                            <ul className="text-xs text-gray-200 list-disc ml-5 space-y-1">
                              <li>Rôles clairs (présidence, secrétariat, owner décision).</li>
                              <li>Responsables d’actions identifiés (pas “l’équipe”).</li>
                              <li>RACI appliqué quand ça touche plusieurs équipes.</li>
                              <li>Un owner “budget” (ou un responsable enveloppe) pour éviter le flou.</li>
                            </ul>
                          </div>
                          <div className="rounded-lg bg-slate-900/40 border border-emerald-500/15 p-3">
                            <p className="text-xs font-bold text-emerald-200 mb-1">Process</p>
                            <ul className="text-xs text-gray-200 list-disc ml-5 space-y-1">
                              <li>Cadence + ODJ type + limite de sujets.</li>
                              <li>Décision = écrite (autorisé/refusé/sous conditions).</li>
                              <li>Actions SMART suivies jusqu’à “done”.</li>
                              <li>Communication régulière (ce qui change + comment appliquer).</li>
                              <li>Budget: enveloppes, arbitrages, seuils d’approbation, suivi budget vs réel.</li>
                            </ul>
                          </div>
                          <div className="rounded-lg bg-slate-900/40 border border-emerald-500/15 p-3">
                            <p className="text-xs font-bold text-emerald-200 mb-1">Tech / Preuves</p>
                            <ul className="text-xs text-gray-200 list-disc ml-5 space-y-1">
                              <li>Un “Decision Packet” 1 page pour tout sujet important.</li>
                              <li>Registre décisions + registre actions (source de vérité unique).</li>
                              <li>Preuves attachées quand requis (permissions, clauses, captures, logs).</li>
                              <li>Volet coûts: estimation (setup + mensuel) + budget alloué + dépenses réelles (traçables).</li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="rounded-xl bg-white/5 border border-emerald-500/15 p-4">
                          <p className="font-semibold text-white mb-2">0 — Ad-hoc</p>
                          <p className="text-gray-200">
                            <strong className="text-white">Ce qui est vrai</strong>: on discute, on “sent” la décision, mais rien n’est écrit. Les sujets reviennent.
                          </p>
                          <p className="text-gray-200 mt-2">
                            <strong className="text-white">Preuves</strong>: aucune (ou notes privées, non partageables).
                          </p>
                          <p className="text-emerald-200 mt-3 font-semibold">Pour passer à 1</p>
                          <ul className="list-disc ml-5 mt-1 space-y-1">
                            <li>Choisir une cadence (mensuelle).</li>
                            <li>Nommer 2–3 rôles minimum (présidence, secrétariat, owner décisions).</li>
                            <li>Décider où vit la “source de vérité” (un registre, pas des chats).</li>
                          </ul>
                        </div>

                        <div className="rounded-xl bg-white/5 border border-emerald-500/15 p-4">
                          <p className="font-semibold text-white mb-2">1 — Informel</p>
                          <p className="text-gray-200">
                            <strong className="text-white">Ce qui est vrai</strong>: rencontres présentes, mais ODJ variable; décisions/actions pas systématiquement capturées.
                          </p>
                          <p className="text-gray-200 mt-2">
                            <strong className="text-white">Preuves</strong>: comptes rendus inégaux, actions dispersées, pas de registre.
                          </p>
                          <p className="text-emerald-200 mt-3 font-semibold">Pour passer à 2 (et atteindre le jalon 1.5)</p>
                          <ul className="list-disc ml-5 mt-1 space-y-1">
                            <li>ODJ type + règle des 3 sujets.</li>
                            <li>Procès-verbal standard (décisions + actions + owners).</li>
                            <li>Jalon 1.5: cadence + rôles + ODJ + un minimum de suivi (même si imparfait).</li>
                          </ul>
                        </div>

                        <div className="rounded-xl bg-white/5 border border-emerald-500/15 p-4">
                          <p className="font-semibold text-white mb-2">2 — Comité structuré (AS-IS)</p>
                          <p className="text-gray-200">
                            <strong className="text-white">Ce qui est vrai</strong>: cadence, rôles, ODJ. On exécute, mais la traçabilité n’est pas “par défaut”.
                          </p>
                          <p className="text-gray-200 mt-2">
                            <strong className="text-white">Preuves</strong>: comptes rendus OK, mais décisions/actions parfois absentes, ou impossibles à retrouver.
                          </p>
                          <p className="text-emerald-200 mt-3 font-semibold">Pour passer à 3 (et atteindre le jalon 2.5)</p>
                          <ul className="list-disc ml-5 mt-1 space-y-1">
                            <li><strong className="text-white">Registre des décisions</strong> obligatoire (décision → raison → risque → conditions → owner → date → date de révision).</li>
                            <li><strong className="text-white">Registre des actions SMART</strong> obligatoire (owner, échéance, statut, dépendances, résultat attendu).</li>
                            <li><strong className="text-white">Communication régulière</strong>: décisions majeures + ce qui change + comment appliquer (mensuel ou trimestriel, mais systématique).</li>
                            <li><strong className="text-white">Budget</strong>: chaque décision importante inclut coût estimé (setup + mensuel) + enveloppe touchée + règle d’approbation + “budget vs réel” revu régulièrement.</li>
                            <li>Jalon 2.5: “si ce n’est pas dans les registres, ça n’existe pas”.</li>
                          </ul>
                        </div>

                        <div className="rounded-xl bg-white/5 border border-emerald-500/15 p-4">
                          <p className="font-semibold text-white mb-2">3 — Décisions traçables</p>
                          <p className="text-gray-200">
                            <strong className="text-white">Ce qui est vrai</strong>: chaque sujet important = décision écrite + actions suivies. On peut expliquer “pourquoi” en 30 secondes.
                          </p>
                          <p className="text-gray-200 mt-2">
                            <strong className="text-white">Preuves</strong>: registres propres + décisions réutilisables + actions fermées (historique).
                          </p>
                          <p className="text-emerald-200 mt-3 font-semibold">Pour passer à 4</p>
                          <ul className="list-disc ml-5 mt-1 space-y-1">
                            <li>Standardiser le <strong className="text-white">Gate (point de contrôle)</strong> (autorisé / refusé / autorisé sous conditions) pour les sujets à risque.</li>
                            <li>Exiger des <strong className="text-white">preuves minimales</strong> quand requis (permissions, clauses, captures, logs, EFVP si applicable).</li>
                            <li>Ajouter une <strong className="text-white">date de révision</strong> aux décisions majeures (les exceptions expirent).</li>
                          </ul>
                        </div>

                        <div className="rounded-xl bg-white/5 border border-emerald-500/15 p-4">
                          <p className="font-semibold text-white mb-2">4 — Points de contrôle + preuves</p>
                          <p className="text-gray-200">
                            <strong className="text-white">Ce qui est vrai</strong>: décisions défendables (audit/incident). Les exceptions sont temporaires et revues.
                          </p>
                          <p className="text-gray-200 mt-2">
                            <strong className="text-white">Preuves</strong>: décisions avec preuves attachées + révisions planifiées + exceptions gérées.
                          </p>
                          <p className="text-emerald-200 mt-3 font-semibold">Pour passer à 5</p>
                          <ul className="list-disc ml-5 mt-1 space-y-1">
                            <li>Rendre le système “autoportant”: moins de dépendance aux personnes.</li>
                            <li>Ajouter 2–3 KPIs simples (ex: % actions closes à temps, délai décision, % décisions avec preuves).</li>
                            <li>Ajouter un KPI budget simple (ex: variance budget vs réel, ou coût récurrent “outil” vs valeur).</li>
                            <li>Rendre l’onboarding “gouvernance” automatique (nouveaux membres = mêmes réflexes).</li>
                          </ul>
                        </div>

                        <div className="rounded-xl bg-white/5 border border-emerald-500/15 p-4">
                          <p className="font-semibold text-white mb-2">5 — Gouvernance réflexe</p>
                          <p className="text-gray-200">
                            <strong className="text-white">Ce qui est vrai</strong>: l’équipe applique spontanément les standards; les décisions sont rapides, cohérentes et stables.
                          </p>
                          <p className="text-gray-400 mt-2 text-xs">
                            Preuves typiques: registre décisions, actions closes, communications, historique, révisions planifiées, budget/enveloppes suivis.
                          </p>
                        </div>
                      </div>
                    </>
                  ) : maturityGuidePilier.id === 9 ? (
                    <>
                      <div className="rounded-xl bg-white/5 border border-emerald-500/15 p-4">
                        <p className="font-semibold text-white mb-2">Lecture holistique (Veille technologique)</p>
                        <ul className="list-disc ml-5 space-y-1.5">
                          <li>
                            <strong className="text-white">Niveau 2</strong> = choix encadrés (décisions en comité, quelques standards).
                          </li>
                          <li>
                            <strong className="text-white">Niveau 3</strong> = roadmap (les choix sont reliés à un plan + suivi).
                          </li>
                          <li>
                            <strong className="text-white">Niveau 4</strong> = architecture cohérente (composants intégrés; complexité maîtrisée; retraits planifiés).
                          </li>
                        </ul>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                          <div className="rounded-lg bg-slate-900/40 border border-emerald-500/15 p-3">
                            <p className="text-xs font-bold text-emerald-200 mb-1">Humain</p>
                            <ul className="text-xs text-gray-200 list-disc ml-5 space-y-1">
                              <li>Un owner par domaine (poste, réseau, data, outils) + backup.</li>
                              <li>Un responsable “standard & décommission” (ce qu’on retire).</li>
                              <li>Arbitrage budget/valeur (enveloppes) plutôt que “nouvel outil = oui”.</li>
                            </ul>
                          </div>
                          <div className="rounded-lg bg-slate-900/40 border border-emerald-500/15 p-3">
                            <p className="text-xs font-bold text-emerald-200 mb-1">Process</p>
                            <ul className="text-xs text-gray-200 list-disc ml-5 space-y-1">
                              <li>Gate (point de contrôle) pour tout nouvel outil / changement majeur.</li>
                              <li>Une catégorie = un standard (éviter les doublons).</li>
                              <li>Roadmap: test → décision → déploiement → retrait.</li>
                              <li>Budget: coût estimé (setup + mensuel) + suivi budget vs réel.</li>
                            </ul>
                          </div>
                          <div className="rounded-lg bg-slate-900/40 border border-emerald-500/15 p-3">
                            <p className="text-xs font-bold text-emerald-200 mb-1">Tech / Preuves</p>
                            <ul className="text-xs text-gray-200 list-disc ml-5 space-y-1">
                              <li>SSO/MFA quand possible (Microsoft Entra) + RBAC.</li>
                              <li>Logs/alertes centralisables (ou au minimum accessibles et revus).</li>
                              <li>“Decision Packet” 1 page + dossier de test + preuve.</li>
                              <li>Inventaire outillage + contrats + coûts + date de révision.</li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="rounded-xl bg-white/5 border border-emerald-500/15 p-4">
                          <p className="font-semibold text-white mb-2">0 — Éparpillé</p>
                          <p className="text-gray-200">
                            <strong className="text-white">Ce qui est vrai</strong>: on teste des outils, on empile, on n’a pas de standard clair.
                          </p>
                          <p className="text-gray-200 mt-2">
                            <strong className="text-white">Preuves</strong>: inventaire incomplet; coûts inconnus; “ça marche chez X”.
                          </p>
                          <p className="text-emerald-200 mt-3 font-semibold">Pour passer à 1</p>
                          <ul className="list-disc ml-5 mt-1 space-y-1">
                            <li>Créer un inventaire outillage (catégorie, owner, coût, contrat, usage).</li>
                            <li>Décider 3 catégories critiques à standardiser (ex: backup, poste, accès).</li>
                          </ul>
                        </div>

                        <div className="rounded-xl bg-white/5 border border-emerald-500/15 p-4">
                          <p className="font-semibold text-white mb-2">1 — Occasionnel</p>
                          <p className="text-gray-200">
                            <strong className="text-white">Ce qui est vrai</strong>: tests ponctuels; décisions au cas par cas; standards partiels.
                          </p>
                          <p className="text-gray-200 mt-2">
                            <strong className="text-white">Preuves</strong>: quelques docs; pas de “test → décision → déploiement” reproductible.
                          </p>
                          <p className="text-emerald-200 mt-3 font-semibold">Pour passer à 2</p>
                          <ul className="list-disc ml-5 mt-1 space-y-1">
                            <li>Définir le Gate minimal (autorisé/refusé/sous conditions).</li>
                            <li>Imposer SSO/MFA + owner + coût estimé pour tout nouvel outil.</li>
                          </ul>
                        </div>

                        <div className="rounded-xl bg-white/5 border border-emerald-500/15 p-4">
                          <p className="font-semibold text-white mb-2">2 — Choix encadrés</p>
                          <p className="text-gray-200">
                            <strong className="text-white">Ce qui est vrai</strong>: décisions en comité; quelques standards; exceptions existantes mais visibles.
                          </p>
                          <p className="text-gray-200 mt-2">
                            <strong className="text-white">Preuves</strong>: décisions écrites + dossiers de test pour certains sujets.
                          </p>
                          <p className="text-emerald-200 mt-3 font-semibold">Pour passer à 3</p>
                          <ul className="list-disc ml-5 mt-1 space-y-1">
                            <li>Relier les décisions à une roadmap (priorités, séquence, échéances).</li>
                            <li>Décider “un standard par catégorie” (et plan de retrait des doublons).</li>
                            <li>Rendre visible le budget outillage (mensuel/récurrent) par catégorie.</li>
                          </ul>
                        </div>

                        <div className="rounded-xl bg-white/5 border border-emerald-500/15 p-4">
                          <p className="font-semibold text-white mb-2">3 — Roadmap</p>
                          <p className="text-gray-200">
                            <strong className="text-white">Ce qui est vrai</strong>: les choix sont planifiés (test→déploiement) et suivis.
                          </p>
                          <p className="text-gray-200 mt-2">
                            <strong className="text-white">Preuves</strong>: roadmap techno + décisions associées + actions closes.
                          </p>
                          <p className="text-emerald-200 mt-3 font-semibold">Pour passer à 4</p>
                          <ul className="list-disc ml-5 mt-1 space-y-1">
                            <li>Intégrer les composants: SSO/MFA, RBAC, logs, réseau, sauvegarde, DLP si applicable.</li>
                            <li>Standardiser les “critères de sortie” (quand un outil est vraiment en prod).</li>
                            <li>Planifier les décommissions (retirer ce qui fait doublon).</li>
                          </ul>
                        </div>

                        <div className="rounded-xl bg-white/5 border border-emerald-500/15 p-4">
                          <p className="font-semibold text-white mb-2">4 — Architecture cohérente</p>
                          <p className="text-gray-200">
                            <strong className="text-white">Ce qui est vrai</strong>: composants intégrés; complexité maîtrisée; moins d’exceptions; retraits planifiés.
                          </p>
                          <p className="text-gray-200 mt-2">
                            <strong className="text-white">Exemples</strong>: un standard backup, un standard poste, un standard accès (Entra), un standard veille/outillage avec Gate.
                          </p>
                          <p className="text-gray-200 mt-2">
                            <strong className="text-white">Preuves</strong>: inventaire complet + coûts connus + dossiers de décision + roadmap + plan de retrait.
                          </p>
                          <p className="text-emerald-200 mt-3 font-semibold">Pour passer à 5</p>
                          <ul className="list-disc ml-5 mt-1 space-y-1">
                            <li>Mesurer 2–3 KPIs (doublons, coûts récurrents, % outils avec SSO/logs).</li>
                            <li>Rendre la veille “utile”: veille → décision → action (pas de bruit).</li>
                            <li>Amélioration continue: revues trimestrielles + dates de révision des décisions.</li>
                          </ul>
                        </div>

                        <div className="rounded-xl bg-white/5 border border-emerald-500/15 p-4">
                          <p className="font-semibold text-white mb-2">5 — Référence</p>
                          <p className="text-gray-200">
                            <strong className="text-white">Ce qui est vrai</strong>: décisions rapides et cohérentes; architecture stable; la veille nourrit l’exécution.
                          </p>
                          <p className="text-gray-400 mt-2 text-xs">
                            Preuves typiques: décisions versionnées, roadmap vivante, coûts maîtrisés, retraits réalisés, baisse des exceptions et du “bricolage”.
                          </p>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="rounded-xl bg-white/5 border border-emerald-500/15 p-4">
                      <p className="font-semibold text-white mb-2">Guide holistique (générique)</p>
                      <p className="text-gray-200">
                        Pour n’importe quel pilier, un niveau est “bon” seulement s’il est <strong className="text-white">observable</strong> (preuves), et pas juste “ressenti”.
                        Utilisez cette grille pour définir votre AS-IS et votre Cible.
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                        <div className="rounded-lg bg-slate-900/40 border border-emerald-500/15 p-3">
                          <p className="text-xs font-bold text-emerald-200 mb-1">Humain</p>
                          <ul className="text-xs text-gray-200 list-disc ml-5 space-y-1">
                            <li>Qui décide? Qui exécute? Qui valide?</li>
                            <li>Compétences minimales (et backup).</li>
                            <li>Rôles appliqués en vrai, pas “sur papier”.</li>
                            <li>Responsable enveloppe budget (qui arbitre et tranche).</li>
                          </ul>
                        </div>
                        <div className="rounded-lg bg-slate-900/40 border border-emerald-500/15 p-3">
                          <p className="text-xs font-bold text-emerald-200 mb-1">Process</p>
                          <ul className="text-xs text-gray-200 list-disc ml-5 space-y-1">
                            <li>Cadence, règles, standards, exceptions.</li>
                            <li>Entrées → décision → actions → revue.</li>
                            <li>“Done” est défini (critères de sortie).</li>
                            <li>Budget: enveloppes, arbitrage, seuils, et suivi budget vs réel.</li>
                          </ul>
                        </div>
                        <div className="rounded-lg bg-slate-900/40 border border-emerald-500/15 p-3">
                          <p className="text-xs font-bold text-emerald-200 mb-1">Tech / Preuves</p>
                          <ul className="text-xs text-gray-200 list-disc ml-5 space-y-1">
                            <li>Outils/automations (si utile).</li>
                            <li>Registres et documents versionnés.</li>
                            <li>Preuves attachées quand requis (audit/incident).</li>
                            <li>Traçabilité coûts: estimés vs réels, et “coûts récurrents” visibles.</li>
                          </ul>
                        </div>
                      </div>
                      <div className="rounded-lg bg-white/5 border border-emerald-500/15 p-3 mt-4">
                        <p className="text-xs font-semibold text-white mb-1">Définition rapide des niveaux (0 → 5)</p>
                        <ul className="text-xs text-gray-200 list-disc ml-5 space-y-1">
                          <li><strong className="text-white">0</strong>: rien de stable; dépend des individus.</li>
                          <li><strong className="text-white">1</strong>: on fait parfois; pas répétable.</li>
                          <li><strong className="text-white">2</strong>: standard défini; application variable.</li>
                          <li><strong className="text-white">3</strong>: standard appliqué; suivi/traçabilité réguliers.</li>
                          <li><strong className="text-white">4</strong>: preuves + revues + exceptions gérées; défendable.</li>
                          <li><strong className="text-white">5</strong>: réflexe d’équipe; amélioration continue; KPIs simples.</li>
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Guide — patching (30/60/90) */}
      <AnimatePresence>
        {isPatchingGuideOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsPatchingGuideOpen(false)}
              className="fixed inset-0 bg-black/70 backdrop-blur-sm z-60"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.98, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.98, y: 10 }}
              className="fixed inset-0 z-61 flex items-center justify-center p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-full max-w-3xl rounded-2xl border border-emerald-500/30 bg-slate-950/90 shadow-2xl shadow-emerald-500/10">
                <div className="flex items-start justify-between gap-3 p-4 border-b border-emerald-500/20">
                  <div>
                    <p className="text-lg font-bold text-white">Guide — Patching 30/60/90 (BZ)</p>
                    <p className="text-sm text-gray-200 mt-1">
                      Objectif: rendre le patching prévisible, mesurable, et défendable (moins de “on patch quand on a le temps”).
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsPatchingGuideOpen(false)}
                    className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                    aria-label="Fermer"
                  >
                    <X className="w-5 h-5 text-gray-300" />
                  </button>
                </div>

                <div className="p-3 sm:p-4 space-y-4 text-sm text-gray-200 max-h-[75vh] overflow-y-auto break-words">
                  <div className="rounded-xl bg-white/5 border border-emerald-500/15 p-4">
                    <p className="font-semibold text-white mb-2">1) Cadence 30 / 60 / 90</p>
                    <ul className="list-disc ml-5 space-y-1.5">
                      <li><strong className="text-white">30 jours</strong>: vulnérabilités critiques (ou exploit actif).</li>
                      <li><strong className="text-white">60 jours</strong>: vulnérabilités élevées.</li>
                      <li><strong className="text-white">90 jours</strong>: vulnérabilités moyennes.</li>
                    </ul>
                    <p className="text-gray-300 text-xs mt-2">
                      Ajustable par BZ, mais l’important est d’avoir une règle simple + une mesure de conformité.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="rounded-xl bg-white/5 border border-emerald-500/15 p-4">
                      <p className="font-semibold text-white mb-2">2) Action1 — mesurer + exécuter</p>
                      <ul className="list-disc ml-5 space-y-1.5">
                        <li><strong className="text-white">Mesurer</strong>: % patché, backlog, machines en retard.</li>
                        <li><strong className="text-white">Déployer</strong>: par lots, fenêtres, rollback si requis.</li>
                        <li><strong className="text-white">Prouver</strong>: rapports (avant/après) + historique.</li>
                      </ul>
                    </div>

                    <div className="rounded-xl bg-white/5 border border-emerald-500/15 p-4">
                      <p className="font-semibold text-white mb-2">3) Exceptions documentées</p>
                      <ul className="list-disc ml-5 space-y-1.5">
                        <li>Raison (ex: brise une app), owner, date.</li>
                        <li>Risque accepté + mesures compensatoires.</li>
                        <li><strong className="text-white">Date de révision</strong> (pas d’exception “permanente”).</li>
                      </ul>
                    </div>
                  </div>

                  <div className="rounded-xl bg-white/5 border border-emerald-500/15 p-4">
                    <p className="font-semibold text-white mb-2">4) Priorisation basée sur le risque</p>
                    <ul className="list-disc ml-5 space-y-1.5">
                      <li>Criticité + exposition (internet vs interne) + actifs concernés.</li>
                      <li>Exploit connu / utilisé activement.</li>
                      <li>Impact (sécurité + opérationnel) et coût d’arrêt.</li>
                    </ul>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

// ============================================================================
// ONGLET — MATURITÉ (SPIDER + BAR) (même principe que PRIsME)
// ============================================================================
type MaturiteItem = { id: number; axe: string; actuel: number; cible: number };

const MATURITE_STORAGE_KEY = 'prisme_bz_securite_maturite_v2';
const MATURITE_STORAGE_KEY_V1 = 'prisme_bz_securite_maturite_v1';

function clamp05HalfStep(n: number) {
  if (Number.isNaN(n)) return 0;
  const clamped = Math.min(5, Math.max(0, n));
  // increments of 0.5
  return Math.round(clamped * 2) / 2;
}

function MaturiteSpiderTab() {
  const initial: MaturiteItem[] = piliersSecurite.map((p) => ({
    id: p.id,
    axe: p.nom,
    actuel: typeof p.actuel === 'number' ? clamp05HalfStep(p.actuel) : 2,
    cible: typeof p.cible === 'number' ? clamp05HalfStep(p.cible) : 3,
  }));

  const [items, setItems] = useState<MaturiteItem[]>(initial);

  useEffect(() => {
    try {
      // v2: mapping by id (stable)
      const rawV2 = localStorage.getItem(MATURITE_STORAGE_KEY);
      if (rawV2) {
        const parsed = JSON.parse(rawV2) as unknown;
        if (!Array.isArray(parsed)) return;
        const byId = new Map<number, MaturiteItem>();
        for (const x of parsed as any[]) {
          if (typeof x?.id !== 'number') continue;
          if (typeof x?.axe !== 'string') continue;
          const actuel = clamp05HalfStep(Number(x.actuel));
          const cible = clamp05HalfStep(Number(x.cible));
          byId.set(x.id, { id: x.id, axe: x.axe, actuel, cible });
        }
        setItems((prev) => prev.map((p) => byId.get(p.id) ?? p));
        return;
      }

      // migration from v1: best-effort mapping by axe label (names)
      const rawV1 = localStorage.getItem(MATURITE_STORAGE_KEY_V1);
      if (!rawV1) return;
      const parsedV1 = JSON.parse(rawV1) as unknown;
      if (!Array.isArray(parsedV1)) return;
      const byAxe = new Map<string, { actuel: number; cible: number }>();
      for (const x of parsedV1 as any[]) {
        if (typeof x?.axe !== 'string') continue;
        const actuel = clamp05HalfStep(Number(x.actuel));
        const cible = clamp05HalfStep(Number(x.cible));
        byAxe.set(x.axe, { actuel, cible });
      }
      setItems((prev) => prev.map((p) => (byAxe.has(p.axe) ? { ...p, ...byAxe.get(p.axe)! } : p)));
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(MATURITE_STORAGE_KEY, JSON.stringify(items));
    } catch {
      // ignore
    }
  }, [items]);

  const labelPilier = (it: MaturiteItem) => `${it.id}. ${it.axe}`;

  const chartData = items.map((item) => {
    const full = labelPilier(item);
    return {
      category: full.length > 22 ? full.substring(0, 22) + '...' : full,
      Actuel: (item.actuel / 5) * 100,
      Cible: (item.cible / 5) * 100,
    };
  });

  const barData = items.map((it) => ({
    ...it,
    label: labelPilier(it),
  }));

  const actuelAverage = (items.reduce((sum, item) => sum + item.actuel, 0) / items.length).toFixed(1);
  const cibleAverage = (items.reduce((sum, item) => sum + item.cible, 0) / items.length).toFixed(1);

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-emerald-600/20 via-teal-600/20 to-emerald-600/20 rounded-xl p-6 border border-emerald-500/30">
        <h3 className="text-2xl font-bold text-center text-white mb-2">Maturité (Spider + Bar)</h3>
        <p className="text-gray-200 text-center text-sm">Évaluation AS-IS vs Cible — Niveaux 0–5 (même principe que PRIsME)</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-4">
          <div className="text-sm text-yellow-300 font-medium">Score Actuel (AS-IS)</div>
          <div className="text-2xl font-bold text-yellow-200">{actuelAverage}/5</div>
          <div className="text-xs text-yellow-400">État actuel</div>
        </div>
        <div className="bg-emerald-500/20 border border-emerald-500/30 rounded-lg p-4">
          <div className="text-sm text-emerald-300 font-medium">Score Cible (TARGET)</div>
          <div className="text-2xl font-bold text-emerald-200">{cibleAverage}/5</div>
          <div className="text-xs text-emerald-400">Objectif</div>
        </div>
      </div>

      <div className="bg-white/5 rounded-xl p-6 border border-emerald-500/20">
        <h4 className="text-xl font-bold text-white mb-4">Graphique Radar — maturité Comité Sécurité</h4>
        <div className="h-96 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={chartData} margin={{ top: 20, right: 80, bottom: 20, left: 80 }}>
              <PolarGrid stroke="#4b5563" />
              <PolarAngleAxis dataKey="category" tick={{ fontSize: 11, fill: '#d1d5db' }} />
              <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10, fill: '#9ca3af' }} tickCount={6} />

              <Radar
                name="Actuel (AS-IS)"
                dataKey="Actuel"
                stroke="#fbbf24"
                fill="#fbbf24"
                fillOpacity={0.2}
                strokeWidth={3}
                dot={{ fill: '#fbbf24', strokeWidth: 2, r: 4 }}
              />

              <Radar
                name="Cible (TARGET)"
                dataKey="Cible"
                stroke="#10b981"
                fill="#10b981"
                fillOpacity={0.1}
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={{ fill: '#10b981', strokeWidth: 2, r: 3 }}
              />

              <Legend wrapperStyle={{ paddingTop: '20px', color: '#d1d5db' }} iconType="circle" />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white/5 rounded-xl p-6 border border-emerald-500/20">
        <h4 className="text-xl font-bold text-white mb-4">Bar Chart — détail par pilier</h4>
        <div className="h-96 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={barData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#4b5563" />
              <XAxis dataKey="label" angle={-35} textAnchor="end" height={120} tick={{ fontSize: 10, fill: '#d1d5db' }} />
              <YAxis domain={[0, 5]} tick={{ fontSize: 10, fill: '#d1d5db' }} />
              <Tooltip contentStyle={{ backgroundColor: '#0b1220', border: '1px solid #4b5563', borderRadius: '8px' }} labelStyle={{ color: '#d1d5db' }} />
              <Legend />
              <Bar dataKey="actuel" fill="#fbbf24" name="Actuel (AS-IS)" />
              <Bar dataKey="cible" fill="#10b981" name="Cible (TARGET)" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white/5 rounded-xl p-6 border border-emerald-500/20">
        <h4 className="text-lg font-bold text-white mb-3">Ajuster AS-IS / Cible (0–5)</h4>
        <div className="space-y-3">
          {items.map((it) => (
            <div key={it.id} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center bg-slate-900/30 rounded-xl border border-emerald-500/10 p-3">
              <div className="text-sm text-gray-200 font-semibold">{labelPilier(it)}</div>
              <label className="flex items-center justify-between gap-3 text-sm text-gray-200">
                <span className="text-yellow-300 font-semibold">AS-IS</span>
                <select
                  className="bg-slate-950/60 border border-white/10 rounded-lg px-2 py-1 text-sm text-gray-100"
                  value={it.actuel}
                  onChange={(e) => {
                    const v = clamp05HalfStep(Number(e.target.value));
                    setItems((prev) => prev.map((p) => (p.id === it.id ? { ...p, actuel: v } : p)));
                  }}
                >
                  {[0, 0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex items-center justify-between gap-3 text-sm text-gray-200">
                <span className="text-emerald-300 font-semibold">Cible</span>
                <select
                  className="bg-slate-950/60 border border-white/10 rounded-lg px-2 py-1 text-sm text-gray-100"
                  value={it.cible}
                  onChange={(e) => {
                    const v = clamp05HalfStep(Number(e.target.value));
                    setItems((prev) => prev.map((p) => (p.id === it.id ? { ...p, cible: v } : p)));
                  }}
                >
                  {[0, 0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-400 mt-3">
          Astuce: ces valeurs sont sauvegardées localement (sur ton navigateur) pour faciliter le travail en parallèle avec l’onglet Cadre holistique.
        </p>
      </div>

      <div className="bg-white/5 rounded-xl p-4 border border-emerald-500/20">
        <h4 className="text-lg font-bold text-white mb-3">Échelle de maturité</h4>
        <ul className="space-y-2 text-gray-200 text-sm">
          <li className="flex items-center gap-2"><span className="font-semibold text-emerald-300">0 =</span><span>Inexistant</span></li>
          <li className="flex items-center gap-2"><span className="font-semibold text-emerald-300">1 =</span><span>POC / Expérimentation</span></li>
          <li className="flex items-center gap-2"><span className="font-semibold text-emerald-300">2 =</span><span>Adoption structurée</span></li>
          <li className="flex items-center gap-2"><span className="font-semibold text-emerald-300">3 =</span><span>Maturité opérationnelle</span></li>
          <li className="flex items-center gap-2"><span className="font-semibold text-emerald-300">4 =</span><span>Excellence</span></li>
          <li className="flex items-center gap-2"><span className="font-semibold text-emerald-300">5 =</span><span>Référence marché</span></li>
        </ul>
      </div>
    </div>
  );
}


type RaciLetter = 'R' | 'A' | 'C' | 'I';
type RaciMode = 'as-is' | 'to-be';
type RaciPersonId =
  | 'francois'
  | 'vincent'
  | 'mikael'
  | 'dany'
  | 'samuel'
  | 'michaelRoy'
  | 'jeanPierreSac'
  | 'invites'
  | 'martin'
  | 'ericDurand'
  | 'ericHains'
  | 'richard';

type RaciPerson = {
  id: RaciPersonId;
  name: string;
  shortName: string;
  title: string;
  group: 'core' | 'secondary';
  watchout: string;
};

type RaciAssignments = Partial<Record<RaciPersonId, RaciLetter[]>>;

type RaciRow = {
  id: string;
  label: string;
  description: string;
  context: string;
  examples: string[];
  notOwned: string[];
  asIs: RaciAssignments;
  toBe: RaciAssignments;
};

type RaciSection = {
  id: string;
  title: string;
  subtitle: string;
  rows: RaciRow[];
};

type SelectedRaciCell = {
  rowId: string;
  personId: RaciPersonId;
  letter: RaciLetter;
};

const raciLetterMeta: Record<
  RaciLetter,
  {
    label: string;
    definition: string;
    badgeClass: string;
    buttonClass: string;
  }
> = {
  R: {
    label: 'Responsible',
    definition: 'Porte l’exécution concrète, coordonne la livraison et maintient le mouvement jusqu’à la preuve.',
    badgeClass: 'border-emerald-400/40 bg-emerald-500/15 text-emerald-200',
    buttonClass: 'border-emerald-400/40 bg-emerald-500/15 text-emerald-100 hover:bg-emerald-500/25',
  },
  A: {
    label: 'Accountable',
    definition: 'Assume le résultat final, tranche les arbitrages et reste le propriétaire unique du sujet.',
    badgeClass: 'border-amber-400/40 bg-amber-500/15 text-amber-200',
    buttonClass: 'border-amber-400/40 bg-amber-500/15 text-amber-100 hover:bg-amber-500/25',
  },
  C: {
    label: 'Consulted',
    definition: 'Apporte expertise, objections utiles et angles morts avant la décision ou la mise en œuvre.',
    badgeClass: 'border-sky-400/40 bg-sky-500/15 text-sky-200',
    buttonClass: 'border-sky-400/40 bg-sky-500/15 text-sky-100 hover:bg-sky-500/25',
  },
  I: {
    label: 'Informed',
    definition: 'Est tenu au courant, comprend les impacts et relaie ce qui doit l’être dans son périmètre.',
    badgeClass: 'border-slate-400/40 bg-slate-500/15 text-slate-200',
    buttonClass: 'border-slate-400/40 bg-slate-500/15 text-slate-100 hover:bg-slate-500/25',
  },
};

const raciPeople: RaciPerson[] = [
  {
    id: 'francois',
    name: 'François',
    shortName: 'François',
    title: 'Président / gouvernance',
    group: 'core',
    watchout: 'Doit garder la hauteur de vue et éviter de retomber dans le micro-détail opérationnel.',
  },
  {
    id: 'vincent',
    name: 'Vincent',
    shortName: 'Vincent',
    title: 'VP TI / sécurité',
    group: 'core',
    watchout: 'Risque naturel de surcharge: trop de R sans relais support = goulot d’étranglement.',
  },
  {
    id: 'mikael',
    name: 'Mikael',
    shortName: 'Mikael',
    title: 'Secrétariat / cyber',
    group: 'core',
    watchout: 'Ne doit pas devenir propriétaire implicite de tout simplement parce qu’il documente bien.',
  },
  {
    id: 'dany',
    name: 'Dany',
    shortName: 'Dany',
    title: 'Sponsor / arbitrage',
    group: 'core',
    watchout: 'Doit commanditer, arbitrer et débloquer — sans se faire aspirer dans l’exécution quotidienne.',
  },
  {
    id: 'samuel',
    name: 'Samuel',
    shortName: 'Samuel',
    title: 'Cloud / architecture',
    group: 'core',
    watchout: 'À engager tôt dès qu’une décision touche l’architecture cible ou les intégrations.',
  },
  {
    id: 'michaelRoy',
    name: 'Michael Roy',
    shortName: 'Michael Roy',
    title: 'Ventes / voix client',
    group: 'core',
    watchout: 'Sa valeur est en amont: risques client, adoption, promesse commerciale et impacts terrain.',
  },
  {
    id: 'jeanPierreSac',
    name: 'Jean-Pierre / SAC',
    shortName: 'Jean-Pierre / SAC',
    title: 'Support & exploitation',
    group: 'core',
    watchout: 'À impliquer assez tôt pour éviter des standards théoriques impossibles à opérer.',
  },
  {
    id: 'invites',
    name: 'Invités / experts ponctuels',
    shortName: 'Invités',
    title: 'Expertise ciblée',
    group: 'core',
    watchout: 'Mandat et décision attendue doivent être clairs, sinon ils ajoutent du bruit au lieu d’éclairer.',
  },
  {
    id: 'martin',
    name: 'Martin Carrier',
    shortName: 'Martin',
    title: 'Télécom',
    group: 'secondary',
    watchout: 'À activer quand réseau, voix, télécom ou dépendances terrain changent réellement.',
  },
  {
    id: 'ericDurand',
    name: 'Éric Durand',
    shortName: 'Éric D.',
    title: 'Programmation',
    group: 'secondary',
    watchout: 'À consulter tôt sur les impacts applicatifs; trop tard = dette ou solutions bricolées.',
  },
  {
    id: 'ericHains',
    name: 'Éric Hains',
    shortName: 'Éric H.',
    title: 'Parc / support terrain',
    group: 'secondary',
    watchout: 'Doit avoir une place claire dans le TO-BE sur le parc, le patching et l’exploitation terrain.',
  },
  {
    id: 'richard',
    name: 'Richard',
    shortName: 'Richard',
    title: 'Support / opérations',
    group: 'secondary',
    watchout: 'Risque classique: être informé trop tard alors qu’il porte ensuite l’impact sur le terrain.',
  },
];

const raciSections: RaciSection[] = [
  {
    id: 'transversal',
    title: 'Section 1 — Responsabilités transversales de gouvernance',
    subtitle: 'Ce qui fait tenir le comité: arbitrage, rituels, standards, budget, communication et exceptions.',
    rows: [
      {
        id: 'transversal-presidence',
        label: 'Présider le comité et arbitrer les décisions clés',
        description: 'Présidence, arbitrage et tenue de ligne',
        context:
          'Le comité doit sortir avec des décisions défendables, pas seulement des échanges. Cette responsabilité garde la ligne entre risque, vitesse, budget et faisabilité.',
        examples: [
          'Trancher “autorisé / refusé / sous conditions” sur un outil, une exception ou un standard.',
          'Couper un sujet immature et exiger le bon niveau de preuve avant de décider.',
          'Arbitrer entre urgence opérationnelle et cohérence PRIsME.',
        ],
        notOwned: [
          'Le détail d’exécution des billets TactiCE.',
          'Le support quotidien ou la gestion de file opérationnelle.',
          'La rédaction technique détaillée à la place des responsables.',
        ],
        asIs: {
          francois: ['A'],
          vincent: ['R'],
          mikael: ['R'],
          dany: ['C'],
          samuel: ['C'],
          michaelRoy: ['C'],
          jeanPierreSac: ['I'],
          invites: ['I'],
          martin: ['I'],
          ericDurand: ['I'],
          ericHains: ['I'],
          richard: ['I'],
        },
        toBe: {
          francois: ['A'],
          vincent: ['R'],
          mikael: ['R'],
          dany: ['C'],
          samuel: ['C'],
          michaelRoy: ['C'],
          jeanPierreSac: ['C'],
          invites: ['I'],
          martin: ['I'],
          ericDurand: ['I'],
          ericHains: ['I'],
          richard: ['I'],
        },
      },
      {
        id: 'transversal-rituels',
        label: 'Préparer les réunions, traces, décisions et suivis',
        description: 'ODJ, notes, actions et mémoire du comité',
        context:
          'Sans rythme ni traces, la gouvernance redevient du verbal. Cette responsabilité transforme les échanges en décision, action, suivi et historique réutilisable.',
        examples: [
          'Préparer l’ordre du jour et limiter les sujets au bon niveau.',
          'Maintenir registre des décisions, actions SMART et dates de révision.',
          'Fermer la boucle entre réunion, communication et prochaine séance.',
        ],
        notOwned: [
          'Le contenu métier de chaque décision à la place des experts.',
          'Le pilotage final des arbitrages stratégiques.',
          'Le suivi de tous les billets opérationnels sans owner nommé.',
        ],
        asIs: {
          francois: ['C'],
          vincent: ['R'],
          mikael: ['A'],
          dany: ['I'],
          samuel: ['C'],
          michaelRoy: ['I'],
          jeanPierreSac: ['I'],
          invites: ['I'],
          martin: ['I'],
          ericDurand: ['I'],
          ericHains: ['I'],
          richard: ['I'],
        },
        toBe: {
          francois: ['C'],
          vincent: ['R'],
          mikael: ['A'],
          dany: ['I'],
          samuel: ['C'],
          michaelRoy: ['I'],
          jeanPierreSac: ['R'],
          invites: ['I'],
          martin: ['I'],
          ericDurand: ['I'],
          ericHains: ['R'],
          richard: ['R'],
        },
      },
      {
        id: 'transversal-standards',
        label: 'Traduire les enjeux en standards, gates et Decision Packets',
        description: 'Transformer le débat en cadre gouvernable',
        context:
          'Le comité doit rester un référentiel PRIsME: cadrer, simplifier et rendre décisionnable. Cette responsabilité produit les standards, règles et paquets de décision réutilisables.',
        examples: [
          'Documenter un Gate d’approbation pour les outils IA ou les exceptions de sécurité.',
          'Formaliser un standard poste, accès, patching ou surveillance.',
          'Préparer un Decision Packet 1 page avec risques, options, impacts et preuve minimale.',
        ],
        notOwned: [
          'Les procédures d’exécution détaillées TactiCE.',
          'Le code ou les configurations détaillées de mise en œuvre.',
          'La tenue d’inventaire opérationnel au quotidien sans lien de gouvernance.',
        ],
        asIs: {
          francois: ['A'],
          vincent: ['R'],
          mikael: ['R'],
          dany: ['I'],
          samuel: ['C'],
          michaelRoy: ['C'],
          jeanPierreSac: ['C'],
          invites: ['C'],
          martin: ['C'],
          ericDurand: ['C'],
          ericHains: ['I'],
          richard: ['I'],
        },
        toBe: {
          francois: ['A'],
          vincent: ['R'],
          mikael: ['R'],
          dany: ['I'],
          samuel: ['C'],
          michaelRoy: ['C'],
          jeanPierreSac: ['C'],
          invites: ['C'],
          martin: ['C'],
          ericDurand: ['C'],
          ericHains: ['C'],
          richard: ['I'],
        },
      },
      {
        id: 'transversal-sponsor-budget',
        label: 'Commanditer, arbitrer le budget et sécuriser les moyens',
        description: 'Sponsor, arbitrage budget et capacité',
        context:
          'La gouvernance reste théorique si personne ne sécurise le temps, les enveloppes et les arbitrages quand il faut choisir. Ici, le sponsor protège la capacité d’agir.',
        examples: [
          'Allouer un budget pour un standard outil ou une correction structurante.',
          'Décider quand une exception coûte trop cher en risque ou en complexité.',
          'Arbitrer la priorité entre plusieurs chantiers concurrents.',
        ],
        notOwned: [
          'L’exécution détaillée des chantiers financés.',
          'Le suivi quotidien des heures et des tâches.',
          'La validation technique de fond sans avis des responsables.',
        ],
        asIs: {
          francois: ['R'],
          vincent: ['R'],
          mikael: ['I'],
          dany: ['A'],
          samuel: ['C'],
          michaelRoy: ['C'],
          jeanPierreSac: ['I'],
          invites: ['I'],
          martin: ['I'],
          ericDurand: ['I'],
          ericHains: ['I'],
          richard: ['I'],
        },
        toBe: {
          francois: ['R'],
          vincent: ['R'],
          mikael: ['I'],
          dany: ['A'],
          samuel: ['C'],
          michaelRoy: ['C'],
          jeanPierreSac: ['C'],
          invites: ['I'],
          martin: ['C'],
          ericDurand: ['I'],
          ericHains: ['I'],
          richard: ['I'],
        },
      },
      {
        id: 'transversal-communication',
        label: 'Communiquer, faire adopter et garder l’alignement inter-équipes',
        description: 'Faire vivre les décisions au-delà du comité',
        context:
          'Une bonne décision qui ne se diffuse pas devient du bruit. Cette responsabilité couvre la communication, l’adoption et la clarté de ce qui change pour BZ.',
        examples: [
          'Produire une synthèse trimestrielle des priorités et décisions majeures.',
          'Expliciter ce qui change pour le support, les ventes ou les équipes terrain.',
          'Identifier les sujets où il faut accompagner davantage l’adoption.',
        ],
        notOwned: [
          'La formation détaillée de chaque équipe sur tous les outils.',
          'Le marketing ou la communication externe.',
          'Le support de proximité sans relais des équipes concernées.',
        ],
        asIs: {
          francois: ['A'],
          vincent: ['R'],
          mikael: ['R'],
          dany: ['I'],
          samuel: ['C'],
          michaelRoy: ['C'],
          jeanPierreSac: ['C'],
          invites: ['I'],
          martin: ['I'],
          ericDurand: ['I'],
          ericHains: ['I'],
          richard: ['I'],
        },
        toBe: {
          francois: ['A'],
          vincent: ['R'],
          mikael: ['R'],
          dany: ['I'],
          samuel: ['C'],
          michaelRoy: ['C'],
          jeanPierreSac: ['R'],
          invites: ['I'],
          martin: ['I'],
          ericDurand: ['I'],
          ericHains: ['C'],
          richard: ['C'],
        },
      },
      {
        id: 'transversal-exceptions',
        label: 'Gérer les exceptions, escalades et risques acceptés',
        description: 'Clarté sur ce qui sort du standard — et jusqu’à quand',
        context:
          'Les exceptions sont normales; les exceptions sans propriétaire ni date de révision sont dangereuses. Cette responsabilité garde les écarts visibles, assumés et temporaires.',
        examples: [
          'Valider une exception MFA ou patching avec date de révision.',
          'Escalader une dérive qui dépasse le niveau acceptable de risque.',
          'Exiger des mesures compensatoires quand le standard n’est pas tenable immédiatement.',
        ],
        notOwned: [
          'Le traitement technique détaillé de chaque incident ou billet.',
          'L’acceptation tacite des écarts “par habitude”.',
          'La gestion de crise complète à la place des playbooks de réponse.',
        ],
        asIs: {
          francois: ['C'],
          vincent: ['A'],
          mikael: ['R'],
          dany: ['C'],
          samuel: ['C'],
          michaelRoy: ['I'],
          jeanPierreSac: ['R'],
          invites: ['I'],
          martin: ['I'],
          ericDurand: ['I'],
          ericHains: ['C'],
          richard: ['C'],
        },
        toBe: {
          francois: ['C'],
          vincent: ['A'],
          mikael: ['R'],
          dany: ['C'],
          samuel: ['C'],
          michaelRoy: ['I'],
          jeanPierreSac: ['R'],
          invites: ['I'],
          martin: ['I'],
          ericDurand: ['I'],
          ericHains: ['R'],
          richard: ['R'],
        },
      },
    ],
  },
  {
    id: 'pillars',
    title: 'Section 2 — RACI par pilier holistique (9 piliers)',
    subtitle: 'Lecture par capacité de gouvernance et de maturité: qui arbitre, qui porte, qui conseille, qui doit être tenu au courant.',
    rows: [
      {
        id: 'pillar-1-governance',
        label: '1. Gouvernance & décisions (Comité)',
        description: 'Cadence, registre, arbitrage, traçabilité',
        context:
          'Ce pilier encadre la qualité globale du comité: cadence, décisions, registres, budget et revues. C’est la colonne vertébrale de la gouvernance.',
        examples: [
          'Maintenir les registres décisions / actions / preuves comme source de vérité.',
          'Mesurer la discipline du comité: délais, actions fermées, exceptions revues.',
          'Rendre les décisions compréhensibles et défendables en audit ou incident.',
        ],
        notOwned: [
          'L’exécution opérationnelle des actions décidées.',
          'Les rituels d’équipes locales sans enjeu de gouvernance.',
          'Les tâches administratives non liées aux décisions du comité.',
        ],
        asIs: {
          francois: ['A'],
          vincent: ['R'],
          mikael: ['R'],
          dany: ['C'],
          samuel: ['C'],
          michaelRoy: ['C'],
          jeanPierreSac: ['C'],
          invites: ['I'],
          martin: ['I'],
          ericDurand: ['I'],
          ericHains: ['I'],
          richard: ['I'],
        },
        toBe: {
          francois: ['A'],
          vincent: ['R'],
          mikael: ['R'],
          dany: ['C'],
          samuel: ['C'],
          michaelRoy: ['C'],
          jeanPierreSac: ['C'],
          invites: ['I'],
          martin: ['I'],
          ericDurand: ['I'],
          ericHains: ['I'],
          richard: ['I'],
        },
      },
      {
        id: 'pillar-2-ai',
        label: '2. IA responsable & cadre (NIST AI RMF)',
        description: 'Usage IA, garde-fous, preuves fournisseur et validation humaine',
        context:
          'Ce pilier définit comment BZ accélère avec l’IA sans perdre la maîtrise des données, de la conformité, de la qualité ni du jugement humain.',
        examples: [
          'Évaluer un outil IA avec Decision Packet, DPA, données, risques et contrôles.',
          'Tenir le registre IA: cas d’usage, owners, niveau de risque, date de révision.',
          'Statuer sur les usages permis, interdits ou autorisés sous conditions.',
        ],
        notOwned: [
          'La rédaction de prompts ou de playbooks opérationnels détaillés.',
          'Le remplacement du jugement humain par l’outil.',
          'L’intégration technique applicative sans coordination avec les responsables concernés.',
        ],
        asIs: {
          francois: ['A'],
          vincent: ['R'],
          mikael: ['R'],
          dany: ['C'],
          samuel: ['R'],
          michaelRoy: ['C'],
          jeanPierreSac: ['I'],
          invites: ['C'],
          martin: ['I'],
          ericDurand: ['C'],
          ericHains: ['I'],
          richard: ['I'],
        },
        toBe: {
          francois: ['A'],
          vincent: ['R'],
          mikael: ['R'],
          dany: ['C'],
          samuel: ['R'],
          michaelRoy: ['C'],
          jeanPierreSac: ['I'],
          invites: ['C'],
          martin: ['I'],
          ericDurand: ['R'],
          ericHains: ['I'],
          richard: ['I'],
        },
      },
      {
        id: 'pillar-3-park',
        label: '3. Parc informatique BZ (infrastructure et postes)',
        description: 'Baseline, inventaire, support et cycle de vie',
        context:
          'Ce pilier couvre la base matérielle et logicielle qui doit devenir prévisible, standardisée et maintenable pour BZ.',
        examples: [
          'Définir la baseline des postes et de l’infrastructure supportée.',
          'Garder une source de vérité de l’inventaire et du cycle de vie.',
          'Faire respecter les standards de remplacement, effacement et support.',
        ],
        notOwned: [
          'La résolution ponctuelle de chaque billet de support.',
          'Les variantes individuelles non justifiées par une vraie exception.',
          'Les décisions applicatives hors périmètre parc / infra.',
        ],
        asIs: {
          francois: ['C'],
          vincent: ['A'],
          mikael: ['C'],
          dany: ['I'],
          samuel: ['C'],
          michaelRoy: ['I'],
          jeanPierreSac: ['R'],
          invites: ['I'],
          martin: ['I'],
          ericDurand: ['I'],
          ericHains: ['C'],
          richard: ['C'],
        },
        toBe: {
          francois: ['C'],
          vincent: ['A'],
          mikael: ['C'],
          dany: ['C'],
          samuel: ['C'],
          michaelRoy: ['I'],
          jeanPierreSac: ['R'],
          invites: ['I'],
          martin: ['C'],
          ericDurand: ['I'],
          ericHains: ['R'],
          richard: ['R'],
        },
      },
      {
        id: 'pillar-4-identity',
        label: '4. Identité & accès',
        description: 'MFA, privilèges, passkeys, revues d’accès',
        context:
          'Ce pilier protège la première surface d’attaque: qui a accès à quoi, avec quel niveau de contrôle et selon quelles exceptions.',
        examples: [
          'Piloter MFA, passkeys/FIDO2, comptes admin et revues d’accès.',
          'Décider les exceptions temporaires avec date de révision.',
          'Rendre l’authentification moderne simple et non optionnelle.',
        ],
        notOwned: [
          'La création ponctuelle d’un accès ordinaire hors standard.',
          'Le support de base utilisateur sans enjeu de posture.',
          'Le design applicatif détaillé des rôles sans alignement global.',
        ],
        asIs: {
          francois: ['C'],
          vincent: ['A'],
          mikael: ['R'],
          dany: ['I'],
          samuel: ['R'],
          michaelRoy: ['I'],
          jeanPierreSac: ['C'],
          invites: ['I'],
          martin: ['I'],
          ericDurand: ['I'],
          ericHains: ['I'],
          richard: ['I'],
        },
        toBe: {
          francois: ['C'],
          vincent: ['A'],
          mikael: ['R'],
          dany: ['C'],
          samuel: ['R'],
          michaelRoy: ['I'],
          jeanPierreSac: ['R'],
          invites: ['I'],
          martin: ['I'],
          ericDurand: ['I'],
          ericHains: ['C'],
          richard: ['I'],
        },
      },
      {
        id: 'pillar-5-patching',
        label: '5. Vulnérabilités & patching',
        description: 'Cadence, backlog, couverture et exceptions',
        context:
          'Ce pilier matérialise l’hygiène technique: voir, prioriser, corriger et prouver que le backlog de vulnérabilités diminue réellement.',
        examples: [
          'Définir les SLA 30/60/90 et suivre la couverture réelle.',
          'Gérer les exceptions avec raison, owner, mesures compensatoires et date de révision.',
          'Prioriser selon le risque et non le bruit ambiant.',
        ],
        notOwned: [
          'Le traitement technique détaillé de chaque correctif en comité.',
          'La simple chasse aux CVE sans lien avec l’exposition réelle.',
          'Les exceptions permanentes “parce que ça dérange”.',
        ],
        asIs: {
          francois: ['C'],
          vincent: ['A'],
          mikael: ['R'],
          dany: ['I'],
          samuel: ['C'],
          michaelRoy: ['I'],
          jeanPierreSac: ['R'],
          invites: ['I'],
          martin: ['I'],
          ericDurand: ['I'],
          ericHains: ['C'],
          richard: ['C'],
        },
        toBe: {
          francois: ['C'],
          vincent: ['A'],
          mikael: ['R'],
          dany: ['I'],
          samuel: ['C'],
          michaelRoy: ['I'],
          jeanPierreSac: ['R'],
          invites: ['I'],
          martin: ['I'],
          ericDurand: ['I'],
          ericHains: ['R'],
          richard: ['R'],
        },
      },
      {
        id: 'pillar-6-data',
        label: '6. Données & conformité (Loi 25)',
        description: 'EFVP, classification, rétention et preuves',
        context:
          'Ce pilier protège la confiance et la défendabilité: savoir quelles données sont sensibles, qui y touche, combien de temps, et selon quelles obligations.',
        examples: [
          'Déterminer quand une EFVP est requise et documenter la décision.',
          'Encadrer rétention, partage, accès et preuves fournisseur.',
          'Rendre l’état des données défendable en audit ou incident.',
        ],
        notOwned: [
          'Les manipulations quotidiennes des utilisateurs sans enjeu de gouvernance.',
          'La négociation juridique détaillée au cas par cas sans coordination.',
          'La conformité “sur papier” sans contrôle ni preuve.',
        ],
        asIs: {
          francois: ['A'],
          vincent: ['C'],
          mikael: ['R'],
          dany: ['C'],
          samuel: ['C'],
          michaelRoy: ['C'],
          jeanPierreSac: ['I'],
          invites: ['C'],
          martin: ['I'],
          ericDurand: ['I'],
          ericHains: ['I'],
          richard: ['I'],
        },
        toBe: {
          francois: ['A'],
          vincent: ['C'],
          mikael: ['R'],
          dany: ['C'],
          samuel: ['C'],
          michaelRoy: ['C'],
          jeanPierreSac: ['I'],
          invites: ['C'],
          martin: ['I'],
          ericDurand: ['C'],
          ericHains: ['I'],
          richard: ['I'],
        },
      },
      {
        id: 'pillar-7-detection',
        label: '7. Détection & surveillance',
        description: 'Logs, alertes, bruit, ownership et revues',
        context:
          'Ce pilier vise à voir plus tôt ce qui dérape et à réduire le bruit pour que les alertes importantes deviennent réellement actionnables.',
        examples: [
          'Nommer les owners des alertes critiques et des tableaux de bord.',
          'Décider les 10 alertes “must have” et leurs seuils.',
          'Mesurer les délais de détection et d’escalade.',
        ],
        notOwned: [
          'L’investigation technique détaillée de chaque événement en comité.',
          'La rétention infinie de logs sans but clair.',
          'Le monitoring gadget sans owner ni seuil utile.',
        ],
        asIs: {
          francois: ['C'],
          vincent: ['A'],
          mikael: ['R'],
          dany: ['I'],
          samuel: ['C'],
          michaelRoy: ['I'],
          jeanPierreSac: ['R'],
          invites: ['I'],
          martin: ['I'],
          ericDurand: ['I'],
          ericHains: ['I'],
          richard: ['I'],
        },
        toBe: {
          francois: ['C'],
          vincent: ['A'],
          mikael: ['R'],
          dany: ['I'],
          samuel: ['C'],
          michaelRoy: ['I'],
          jeanPierreSac: ['R'],
          invites: ['I'],
          martin: ['I'],
          ericDurand: ['I'],
          ericHains: ['C'],
          richard: ['R'],
        },
      },
      {
        id: 'pillar-8-response',
        label: '8. Réponse & reprise',
        description: 'Playbooks, rôles, tabletop, preuves et reprise',
        context:
          'Ce pilier prépare BZ à agir sous pression: clarifier les rôles, tester la réponse, gérer les preuves et apprendre après incident.',
        examples: [
          'Maintenir une procédure incident vivante et testée.',
          'Organiser des tabletop et boucler les actions d’amélioration.',
          'Coordonner communications, preuves et reprise sans improvisation.',
        ],
        notOwned: [
          'L’exécution complète d’un incident en temps réel seulement dans le comité.',
          'La gestion de support courante sans caractère incident.',
          'La fermeture d’un incident sans post-mortem ni apprentissage.',
        ],
        asIs: {
          francois: ['A'],
          vincent: ['R'],
          mikael: ['R'],
          dany: ['C'],
          samuel: ['C'],
          michaelRoy: ['C'],
          jeanPierreSac: ['R'],
          invites: ['C'],
          martin: ['I'],
          ericDurand: ['I'],
          ericHains: ['I'],
          richard: ['I'],
        },
        toBe: {
          francois: ['A'],
          vincent: ['R'],
          mikael: ['R'],
          dany: ['C'],
          samuel: ['C'],
          michaelRoy: ['C'],
          jeanPierreSac: ['R'],
          invites: ['C'],
          martin: ['I'],
          ericDurand: ['I'],
          ericHains: ['C'],
          richard: ['R'],
        },
      },
      {
        id: 'pillar-9-watch',
        label: '9. Veille technologique',
        description: 'Tests, standards, architecture cible et décommission',
        context:
          'Ce pilier garde BZ cohérent dans ses choix technologiques: on teste, on décide, on déploie, on retire le doublon — au lieu d’empiler.',
        examples: [
          'Évaluer une nouvelle brique selon intégration, sécurité, coût et décommission du doublon.',
          'Relier la veille à une roadmap plutôt qu’à des essais isolés.',
          'Décider les standards par catégorie et les critères de sortie.',
        ],
        notOwned: [
          'Les essais opportunistes sans objectif ni sortie claire.',
          'La multiplication d’outils redondants “pour voir”.',
          'Le développement applicatif détaillé d’une solution.',
        ],
        asIs: {
          francois: ['A'],
          vincent: ['R'],
          mikael: ['I'],
          dany: ['C'],
          samuel: ['R'],
          michaelRoy: ['C'],
          jeanPierreSac: ['C'],
          invites: ['C'],
          martin: ['C'],
          ericDurand: ['C'],
          ericHains: ['I'],
          richard: ['I'],
        },
        toBe: {
          francois: ['A'],
          vincent: ['R'],
          mikael: ['C'],
          dany: ['C'],
          samuel: ['R'],
          michaelRoy: ['C'],
          jeanPierreSac: ['C'],
          invites: ['C'],
          martin: ['R'],
          ericDurand: ['R'],
          ericHains: ['I'],
          richard: ['I'],
        },
      },
    ],
  },
];

const raciModeMeta: Record<RaciMode, { label: string; description: string }> = {
  'as-is': {
    label: 'AS-IS',
    description: 'Lecture du fonctionnement actuel: noyau serré, support encore surtout consulté ou informé selon les sujets.',
  },
  'to-be': {
    label: 'TO-BE',
    description: 'Cible V1: gouvernance plus distribuée, avec support / parc / expertise terrain activés plus tôt sur les piliers concernés.',
  },
};

function getRaciAssignments(row: RaciRow, mode: RaciMode) {
  return mode === 'as-is' ? row.asIs : row.toBe;
}

function getRaciBehaviors(letter: RaciLetter, row: RaciRow) {
  if (letter === 'A') {
    return [
      `Clarifier ce que “bon” veut dire pour « ${row.label} » et les critères d’acceptation.`,
      'Trancher les arbitrages, valider les conditions et assumer le résultat final devant le comité.',
      'Vérifier que les R ont les moyens, que les C ont été entendus et que les I reçoivent un message exploitable.',
    ];
  }

  if (letter === 'R') {
    return [
      `Transformer « ${row.label} » en actions, livrables, rythme et preuves concrètes.`,
      'Coordonner les contributeurs utiles, remonter tôt les blocages et fermer la boucle entre décision et exécution.',
      'Maintenir l’état d’avancement visible pour éviter les angles morts entre les rencontres.',
    ];
  }

  if (letter === 'C') {
    return [
      `Apporter expertise, risques, dépendances et objections utiles avant de figer « ${row.label} ».`,
      'Aider à améliorer la décision en nommant les impacts terrain, client, architecture ou support.',
      'Rester exigeant sans prendre le volant à la place du A ou des R.',
    ];
  }

  return [
    `Comprendre la décision sur « ${row.label} » et ses impacts sur son périmètre.`,
    'Relayer ce qui doit changer dans les équipes, routines ou attentes locales.',
    'Remonter rapidement si le contexte change ou si un angle mort majeur apparaît après coup.',
  ];
}

function getRaciPitfalls(letter: RaciLetter, row: RaciRow) {
  if (letter === 'A') {
    return [
      `A trop diffus sur « ${row.label} » = personne ne tranche vraiment.`,
      'Être A sans revoir les conditions ni les dates de révision quand le contexte change.',
      'Descendre trop bas dans l’opérationnel et étouffer les R au lieu de les débloquer.',
    ];
  }

  if (letter === 'R') {
    return [
      `Exécuter « ${row.label} » sans rendre l’avancement visible au A et au comité.`,
      'Attendre la prochaine rencontre pour escalader un blocage déjà critique.',
      'Confondre effort fourni et résultat prouvé.',
    ];
  }

  if (letter === 'C') {
    return [
      `Être consulté trop tard pour influencer réellement « ${row.label} ».`,
      'Donner un avis flou, non actionnable, ou hors périmètre.',
      'Se substituer au A/R sans mandat explicite, ce qui brouille l’ownership.',
    ];
  }

  return [
    `Rester “informé” sans ajuster son périmètre quand « ${row.label} » change.`,
    'Découvrir les impacts en retard parce que l’information n’a pas été relayée localement.',
    'Croire que I = passif alors que la vigilance reste attendue.',
  ];
}

function findFirstRaciCell(mode: RaciMode): SelectedRaciCell | null {
  const priority: RaciLetter[] = ['A', 'R', 'C', 'I'];

  for (const letter of priority) {
    for (const section of raciSections) {
      for (const row of section.rows) {
        const assignments = getRaciAssignments(row, mode);
        for (const person of raciPeople) {
          const letters = assignments[person.id] ?? [];
          if (letters.includes(letter)) {
            return { rowId: row.id, personId: person.id, letter };
          }
        }
      }
    }
  }

  return null;
}

function RolesResponsabilitesTab() {
  const [mode, setMode] = useState<RaciMode>('as-is');
  const [selectedCell, setSelectedCell] = useState<SelectedRaciCell | null>(() => findFirstRaciCell('as-is'));

  const corePeople = useMemo(() => raciPeople.filter((person) => person.group === 'core'), []);
  const secondaryPeople = useMemo(() => raciPeople.filter((person) => person.group === 'secondary'), []);
  const allRows = useMemo(() => raciSections.flatMap((section) => section.rows), []);

  useEffect(() => {
    if (!selectedCell) {
      setSelectedCell(findFirstRaciCell(mode));
      return;
    }

    const row = allRows.find((item) => item.id === selectedCell.rowId);
    if (!row) {
      setSelectedCell(findFirstRaciCell(mode));
      return;
    }

    const letters = getRaciAssignments(row, mode)[selectedCell.personId] ?? [];
    if (!letters.includes(selectedCell.letter)) {
      setSelectedCell(findFirstRaciCell(mode));
    }
  }, [allRows, mode, selectedCell]);

  const compliance = useMemo(() => {
    const entries = allRows.map((row) => {
      const aCount = raciPeople.reduce((count, person) => {
        const letters = getRaciAssignments(row, mode)[person.id] ?? [];
        return count + (letters.includes('A') ? 1 : 0);
      }, 0);

      return { row, aCount };
    });

    return {
      total: entries.length,
      compliant: entries.filter((entry) => entry.aCount === 1).length,
      issues: entries.filter((entry) => entry.aCount !== 1).map((entry) => entry.row.label),
    };
  }, [allRows, mode]);

  const selectedRow = selectedCell ? allRows.find((row) => row.id === selectedCell.rowId) ?? null : null;
  const selectedPerson = selectedCell ? raciPeople.find((person) => person.id === selectedCell.personId) ?? null : null;

  const personSummaries = useMemo(
    () =>
      raciPeople.map((person) => {
        const byLetter: Record<RaciLetter, string[]> = { A: [], R: [], C: [], I: [] };

        allRows.forEach((row) => {
          const letters = getRaciAssignments(row, mode)[person.id] ?? [];
          letters.forEach((letter) => {
            byLetter[letter].push(row.label);
          });
        });

        return {
          person,
          byLetter,
          total: byLetter.A.length + byLetter.R.length + byLetter.C.length + byLetter.I.length,
        };
      }),
    [allRows, mode]
  );

  return (
    <div className="space-y-6">
      <SectionTitle
        title="Rôles & responsabilités"
        subtitle="V1 hybride RACI — lecture AS-IS / TO-BE, responsabilités transversales + 9 piliers holistiques, avec un panneau de sens par cellule."
      />

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 bg-white/5 rounded-xl p-6 border border-emerald-500/20 space-y-3">
          <div className="flex items-center gap-2 text-emerald-200 text-sm font-semibold">
            <Users className="w-4 h-4" />
            Mini intro
          </div>
          <p className="text-sm text-gray-200 leading-relaxed">
            Cette vue remplace le placeholder par une vraie lecture de gouvernance: <strong className="text-white">qui arbitre</strong>,
            <strong className="text-white"> qui porte</strong>, <strong className="text-white">qui doit être consulté</strong> et
            <strong className="text-white"> qui doit être aligné</strong>, sans basculer dans l’opérationnel TactiCE.
          </p>
          <p className="text-sm text-gray-200 leading-relaxed">
            Le <strong className="text-white">AS-IS</strong> montre la mécanique actuelle; le <strong className="text-white">TO-BE</strong> assume
            une version plus mature où le parc, le support et les expertises ponctuelles entrent plus tôt quand le sujet l’exige.
          </p>
        </div>

        <div className="bg-white/5 rounded-xl p-6 border border-emerald-500/20 space-y-4">
          <div>
            <div className="flex items-center gap-2 text-amber-200 text-sm font-semibold mb-2">
              <Target className="w-4 h-4" />
              Règle de gouvernance — un seul A
            </div>
            <p className="text-sm text-gray-200 leading-relaxed">
              Un sujet peut avoir plusieurs R, plusieurs C et plusieurs I. En revanche, il ne doit avoir <strong className="text-white">qu’un seul A</strong>:
              la personne qui tranche, assume le résultat et reste redevable si le contexte se dégrade.
            </p>
          </div>

          <div className="rounded-xl border border-emerald-500/20 bg-slate-950/40 p-4">
            <div className="flex items-center justify-between gap-3 mb-2">
              <span className="text-xs uppercase tracking-[0.2em] text-gray-400">Contrôle V1</span>
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold border ${
                  compliance.compliant === compliance.total
                    ? 'border-emerald-400/40 bg-emerald-500/15 text-emerald-200'
                    : 'border-amber-400/40 bg-amber-500/15 text-amber-200'
                }`}
              >
                {compliance.compliant}/{compliance.total} lignes conformes
              </span>
            </div>
            <p className="text-sm text-gray-200">
              {compliance.compliant === compliance.total
                ? 'Chaque responsabilité affichée garde un propriétaire unique (A).'
                : `Points à corriger: ${compliance.issues.join(' • ')}`}
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white/5 rounded-xl p-4 border border-emerald-500/20">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <p className="text-sm font-semibold text-white">Lecture active: {raciModeMeta[mode].label}</p>
            <p className="text-sm text-gray-300 mt-1">{raciModeMeta[mode].description}</p>
          </div>

          <div className="inline-flex rounded-xl border border-white/10 bg-slate-950/50 p-1 self-start">
            {(['as-is', 'to-be'] as RaciMode[]).map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setMode(option)}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  mode === option ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white' : 'text-gray-300 hover:text-white'
                }`}
              >
                {raciModeMeta[option].label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-4">
          {(['A', 'R', 'C', 'I'] as RaciLetter[]).map((letter) => (
            <span
              key={letter}
              className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold border ${raciLetterMeta[letter].badgeClass}`}
            >
              <span className="text-[11px] font-black tracking-[0.2em]">{letter}</span>
              <span>{raciLetterMeta[letter].label}</span>
            </span>
          ))}
        </div>
      </div>

      {raciSections.map((section) => (
        <div key={section.id} className="bg-white/5 rounded-xl border border-emerald-500/20 overflow-hidden">
          <div className="px-6 py-4 border-b border-emerald-500/15 bg-gradient-to-r from-emerald-600/10 via-teal-600/10 to-transparent">
            <h4 className="text-lg font-bold text-white">{section.title}</h4>
            <p className="text-sm text-gray-300 mt-1">{section.subtitle}</p>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-[1650px] w-full border-collapse">
              <thead>
                <tr className="bg-slate-950/70 text-xs uppercase tracking-[0.18em] text-gray-400">
                  <th className="sticky left-0 z-20 min-w-[330px] px-4 py-3 text-left border-b border-r border-white/10 bg-slate-950/95">
                    Responsabilité
                  </th>
                  <th colSpan={corePeople.length} className="px-4 py-3 text-center border-b border-r border-white/10">
                    Noyau gouvernance
                  </th>
                  <th colSpan={secondaryPeople.length} className="px-4 py-3 text-center border-b border-white/10">
                    Secondaires / expertises ciblées
                  </th>
                </tr>
                <tr className="bg-slate-950/50">
                  <th className="sticky left-0 z-20 px-4 py-3 text-left border-b border-r border-white/10 bg-slate-950/95 text-xs text-gray-400">
                    Intention et lecture du rôle
                  </th>
                  {[...corePeople, ...secondaryPeople].map((person) => (
                    <th key={person.id} className="min-w-[108px] px-2 py-3 border-b border-r border-white/10 last:border-r-0 align-top">
                      <div className="flex flex-col items-center gap-1 text-center">
                        <span className="text-sm font-semibold text-white leading-tight">{person.shortName}</span>
                        <span className="text-[11px] text-gray-400 leading-tight">{person.title}</span>
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold border ${
                            person.group === 'core'
                              ? 'border-emerald-400/30 bg-emerald-500/10 text-emerald-200'
                              : 'border-slate-400/30 bg-slate-500/10 text-slate-200'
                          }`}
                        >
                          {person.group === 'core' ? 'noyau' : 'secondaire'}
                        </span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {section.rows.map((row) => {
                  const assignments = getRaciAssignments(row, mode);

                  return (
                    <tr key={row.id} className="align-top even:bg-white/[0.02]">
                      <td className="sticky left-0 z-10 min-w-[330px] px-4 py-4 border-b border-r border-white/10 bg-slate-950/95">
                        <div className="space-y-2">
                          <div>
                            <p className="text-sm font-semibold text-white leading-snug">{row.label}</p>
                            <p className="text-xs text-emerald-200 mt-1">{row.description}</p>
                          </div>
                          <p className="text-xs text-gray-400 leading-relaxed">{row.context}</p>
                        </div>
                      </td>

                      {[...corePeople, ...secondaryPeople].map((person) => {
                        const letters = assignments[person.id] ?? [];

                        return (
                          <td key={person.id} className="px-2 py-3 border-b border-r border-white/10 last:border-r-0 text-center">
                            {letters.length > 0 ? (
                              <div className="flex flex-wrap items-center justify-center gap-2">
                                {letters.map((letter) => (
                                  <button
                                    key={`${row.id}-${person.id}-${letter}`}
                                    type="button"
                                    onClick={() => setSelectedCell({ rowId: row.id, personId: person.id, letter })}
                                    className={`inline-flex h-8 w-8 items-center justify-center rounded-full border text-xs font-black tracking-[0.15em] transition-colors ${raciLetterMeta[letter].buttonClass} ${
                                      selectedCell?.rowId === row.id &&
                                      selectedCell?.personId === person.id &&
                                      selectedCell?.letter === letter
                                        ? 'ring-2 ring-white/40'
                                        : ''
                                    }`}
                                    title={`${person.name} — ${letter} — ${row.label}`}
                                  >
                                    {letter}
                                  </button>
                                ))}
                              </div>
                            ) : (
                              <span className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/10 bg-white/[0.03] text-gray-600">
                                ·
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      <div className="grid grid-cols-1 xl:grid-cols-[1.15fr_0.85fr] gap-4 items-start">
        <div className="bg-white/5 rounded-xl border border-emerald-500/20 p-6 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm uppercase tracking-[0.2em] text-gray-400">Panneau de lecture</p>
              <h4 className="text-xl font-bold text-white mt-1">Sens du rôle sélectionné</h4>
            </div>
            {selectedCell ? (
              <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold border ${raciLetterMeta[selectedCell.letter].badgeClass}`}>
                <span className="font-black tracking-[0.15em]">{selectedCell.letter}</span>
                <span>{raciLetterMeta[selectedCell.letter].label}</span>
              </span>
            ) : null}
          </div>

          {selectedCell && selectedRow && selectedPerson ? (
            <>
              <div className="rounded-xl border border-white/10 bg-slate-950/40 p-4">
                <p className="text-xs uppercase tracking-[0.18em] text-gray-400">Cellule active</p>
                <p className="text-lg font-semibold text-white mt-2 leading-snug">{selectedRow.label}</p>
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  <span className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold border border-white/10 bg-white/5 text-gray-100">
                    {selectedPerson.name}
                  </span>
                  <span className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold border border-white/10 bg-white/5 text-gray-300">
                    {selectedPerson.title}
                  </span>
                  <span className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold border border-emerald-400/30 bg-emerald-500/10 text-emerald-200">
                    {raciModeMeta[mode].label}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-xl border border-white/10 bg-slate-950/30 p-4">
                  <p className="text-xs uppercase tracking-[0.18em] text-gray-400">Définition</p>
                  <p className="text-sm text-gray-200 leading-relaxed mt-2">
                    <strong className="text-white">{raciLetterMeta[selectedCell.letter].label}</strong> — {raciLetterMeta[selectedCell.letter].definition}
                  </p>
                  <p className="text-sm text-gray-300 leading-relaxed mt-3">{selectedRow.context}</p>
                </div>

                <div className="rounded-xl border border-white/10 bg-slate-950/30 p-4">
                  <p className="text-xs uppercase tracking-[0.18em] text-gray-400">Ce que ça ne possède pas</p>
                  <ul className="mt-2 space-y-2 text-sm text-gray-200">
                    {selectedRow.notOwned.map((item) => (
                      <li key={item} className="flex items-start gap-2">
                        <X className="w-4 h-4 text-red-300 flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-xl border border-white/10 bg-slate-950/30 p-4">
                  <p className="text-xs uppercase tracking-[0.18em] text-gray-400">Comportements attendus</p>
                  <ul className="mt-2 space-y-2 text-sm text-gray-200">
                    {getRaciBehaviors(selectedCell.letter, selectedRow).map((item) => (
                      <li key={item} className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-300 flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="rounded-xl border border-white/10 bg-slate-950/30 p-4">
                  <p className="text-xs uppercase tracking-[0.18em] text-gray-400">Exemples concrets BZ</p>
                  <ul className="mt-2 space-y-2 text-sm text-gray-200">
                    {selectedRow.examples.map((item) => (
                      <li key={item} className="flex items-start gap-2">
                        <ClipboardList className="w-4 h-4 text-sky-300 flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="rounded-xl border border-amber-400/20 bg-amber-500/10 p-4">
                <p className="text-xs uppercase tracking-[0.18em] text-amber-200">Pièges fréquents</p>
                <ul className="mt-2 space-y-2 text-sm text-amber-50/90">
                  {getRaciPitfalls(selectedCell.letter, selectedRow).map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-amber-200 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          ) : (
            <div className="rounded-xl border border-dashed border-white/10 bg-slate-950/20 p-6 text-sm text-gray-400">
              Sélectionner un badge R / A / C / I dans la matrice pour ouvrir la lecture détaillée.
            </div>
          )}
        </div>

        <div className="bg-white/5 rounded-xl border border-emerald-500/20 p-6 space-y-4">
          <div>
            <p className="text-sm uppercase tracking-[0.2em] text-gray-400">Lecture rapide</p>
            <h4 className="text-xl font-bold text-white mt-1">Ce que veut dire la matrice</h4>
          </div>
          <div className="space-y-3 text-sm text-gray-200">
            <p>
              <strong className="text-white">A</strong> n’est pas “celui qui fait tout” — c’est celui qui assume la sortie et garde l’unicité de la responsabilité.
            </p>
            <p>
              <strong className="text-white">R</strong> est là où le travail avance vraiment; trop de R concentrés sur une seule personne = dette future.
            </p>
            <p>
              <strong className="text-white">C</strong> vaut surtout s’il arrive avant la décision; consulté trop tard = friction sans valeur.
            </p>
            <p>
              <strong className="text-white">I</strong> n’est pas décoratif: c’est la couche qui garde les équipes alignées et prévient les mauvaises surprises terrain.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="text-sm uppercase tracking-[0.2em] text-gray-400">Synthèse par personne</p>
            <h4 className="text-xl font-bold text-white mt-1">Highlights A / R / C / I et vigilance</h4>
          </div>
          <span className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold border border-white/10 bg-white/5 text-gray-300">
            Vue {raciModeMeta[mode].label}
          </span>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {personSummaries.map(({ person, byLetter, total }) => (
            <div key={person.id} className="bg-white/5 rounded-xl border border-emerald-500/20 p-5 space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h5 className="text-lg font-bold text-white">{person.name}</h5>
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold border ${
                        person.group === 'core'
                          ? 'border-emerald-400/30 bg-emerald-500/10 text-emerald-200'
                          : 'border-slate-400/30 bg-slate-500/10 text-slate-200'
                      }`}
                    >
                      {person.group === 'core' ? 'noyau' : 'secondaire'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-400 mt-1">{person.title}</p>
                </div>
                <span className="text-xs text-gray-500">{total} attributions</span>
              </div>

              <div className="flex flex-wrap gap-2">
                {(['A', 'R', 'C', 'I'] as RaciLetter[]).map((letter) => (
                  <span
                    key={letter}
                    className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold border ${raciLetterMeta[letter].badgeClass}`}
                  >
                    <span className="font-black tracking-[0.15em]">{letter}</span>
                    <span>{byLetter[letter].length}</span>
                  </span>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(['A', 'R', 'C', 'I'] as RaciLetter[]).map((letter) => (
                  <div key={letter} className="rounded-xl border border-white/10 bg-slate-950/30 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold border ${raciLetterMeta[letter].badgeClass}`}>
                        {letter}
                      </span>
                      <span className="text-xs text-gray-400">{raciLetterMeta[letter].label}</span>
                    </div>
                    {byLetter[letter].length > 0 ? (
                      <ul className="space-y-1.5 text-sm text-gray-200">
                        {byLetter[letter].slice(0, 3).map((item) => (
                          <li key={item} className="leading-snug">• {item}</li>
                        ))}
                        {byLetter[letter].length > 3 ? <li className="text-xs text-gray-500">+ {byLetter[letter].length - 3} autres</li> : null}
                      </ul>
                    ) : (
                      <p className="text-sm text-gray-500">Aucun highlight dans cette vue.</p>
                    )}
                  </div>
                ))}
              </div>

              <div className="rounded-xl border border-amber-400/20 bg-amber-500/10 p-4">
                <p className="text-xs uppercase tracking-[0.18em] text-amber-200">Risque / vigilance</p>
                <p className="text-sm text-amber-50/90 leading-relaxed mt-2">{person.watchout}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


export default function GouvernanceSecuriteModal({ isOpen, onClose }: GouvernanceSecuriteModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('esprit');
  const [isGateGuideOpen, setIsGateGuideOpen] = useState(false);
  const { dates: lexDates } = useLexLastSync();
  const lastAnalysisDate = isOpen ? (lexDates['gouvernance_comite_securite_last_ai_analysis'] ?? null) : null;

  const tabs = useMemo(
    () => [
      { id: 'esprit' as const, label: 'Esprit & Objectifs macro', icon: Eye },
      { id: 'cadre' as const, label: 'Cadre holistique (coaching)', icon: BookOpen },
      { id: 'maturite' as const, label: 'Maturité (Spider + Bar)', icon: Target },
      { id: 'raci' as const, label: 'Rôles & responsabilités', icon: Users },
      { id: 'roadmap' as const, label: 'Roadmap', icon: Calendar },
      { id: 'rituels' as const, label: 'Gouvernance & rituels', icon: ShieldCheck },
    ],
    []
  );

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-br from-slate-900 via-emerald-900/60 to-slate-900 rounded-2xl w-[90vw] h-[90vh] border border-emerald-500/30 shadow-2xl shadow-emerald-500/10 flex flex-col">
              {/* Header */}
              <div className="relative p-3 border-b border-emerald-500/30 bg-gradient-to-r from-emerald-900/40 to-slate-900/40">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    <div className="p-2 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-lg">
                      <ShieldCheck className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-emerald-300 to-teal-300">
                        Gouvernance — Comité TI et Cybersécurité
                      </h2>
                      <p className="text-gray-300 text-xs">
                        Modèle de gouvernance à compléter (François + Vincent)
                      </p>
                      <div className="flex items-center gap-3 mt-2 flex-wrap">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-purple-500/30 border border-purple-400/50 text-xs text-purple-200" title="Disponible dans AI Assistant">
                          <Brain className="w-3.5 h-3.5" />
                          AI
                        </span>
                        {lastAnalysisDate && (
                          <span className="text-gray-400 text-xs flex items-center gap-1.5">
                            <Clock className="w-3.5 h-3.5" />
                            Dernière synchro LEX: {formatLastAnalysisDate(lastAnalysisDate)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <img src="/FP.png" alt="François Delisle" className="w-8 h-8 rounded-full border-2 border-emerald-400" title="François Delisle" />
                      <img src="/VB.png" alt="Vincent Bourassa" className="w-8 h-8 rounded-full border-2 border-emerald-400" title="Vincent Bourassa" />
                    </div>
                  </div>
                  <button onClick={onClose} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors ml-2">
                    <X className="w-5 h-5 text-gray-300" />
                  </button>
                </div>
              </div>

              {/* Tabs Navigation */}
              <div className="border-b border-emerald-500/20 bg-slate-900/50 px-6 pt-3 pb-0 overflow-x-auto">
                <div className="flex gap-2">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-t-lg font-semibold text-sm transition-all whitespace-nowrap ${
                          activeTab === tab.id
                            ? 'bg-gradient-to-b from-emerald-600 to-teal-600 text-white shadow-lg'
                            : 'text-gray-300 hover:text-white hover:bg-white/5'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {tab.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                <AnimatePresence mode="wait">
                  {activeTab === 'esprit' && (
                    <motion.div key="esprit" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} className="space-y-6">
                      <SectionTitle
                        title="Esprit & objectifs macro"
                        subtitle="Un ange gardien: il protège BZ, rend le parc TI solide/efficace/sécuritaire, et tranche avec des standards d’industrie (NIST) — pour nous et pour nos clients."
                      />

                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        <InfoCard
                          title="Pourquoi ce comité existe"
                          icon={<ShieldCheck className="w-5 h-5" />}
                          bullets={[
                            'Être le repère officiel de BZ en cybersécurité et en opérations TI — d’abord pour nos clients, ensuite pour BZ: un ange gardien qui assure des standards d’industrie appliqués, une exécution fiable, et un parc informatique solide, efficace et sécuritaire (chez nous comme dans ce qu’on opère).',
                            'Traduire les meilleures pratiques (industrie, NIST) en standards BZ simples à appliquer, puis en décisions claires (autorisé / refusé / sous conditions) qui se transforment en actions suivies et mesurables.',
                            'Protéger BZ en permettant d’avancer vite et juste: réduire les risques majeurs dans l’ordre (incident de sécurité, incohérence, instabilité, perte de crédibilité client), sans tomber dans le blocage par réflexe.',
                          ]}
                        />
                        <InfoCard
                          title="Ce qu’il protège (et pour qui)"
                          icon={<Wrench className="w-5 h-5" />}
                          bullets={[
                            'BZ (interne): parc informatique, accès, outils, support, et posture sécurité globale (humain/process/tech).',
                            'Clients: ce comité devient la référence de nos départements Cybersécurité et TI, parce qu’il standardise, tranche et documente. Il réduit aussi l’écart entre ce qu’on recommande et ce qu’on applique, en structurant une amélioration continue mesurable.',
                            'L’organisation (collectif): ce comité sert de référence pour “quoi faire” et “quoi ne pas faire”, avec une logique défendable.',
                          ]}
                        />
                        <InfoCard
                          title="Ce qu’il permet (ce que l’équipe gagne)"
                          icon={<CheckCircle2 className="w-5 h-5" />}
                          bullets={[
                            'Des décisions rapides et claires (autorisé / refusé / autorisé sous conditions), basées sur standards et preuves, pas sur des opinions.',
                            'Un parc TI plus stable et plus performant: moins d’improvisation, moins de variations, moins de “bricolage”, plus de constance.',
                            'Une posture sécurité plus robuste: risques connus, contrôles priorisés, procédures prêtes, et amélioration continue.',
                            'Une maturité TI qui progresse en continu: un cadre clair + une mesure (spider) pour piloter, comparer et améliorer sans dépendre des individus.',
                          ]}
                        />
                      </div>

                      <InfoCard
                        title="Comment il décide (Gate / points de contrôle) — la gouvernance vivante"
                        icon={
                          <div className="flex items-center gap-2">
                            <ClipboardList className="w-5 h-5" />
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setIsGateGuideOpen(true);
                              }}
                              className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2 py-1 text-[11px] text-emerald-100 border border-emerald-300/30 hover:bg-white/15 transition-colors"
                              title="Guide: c’est quoi un Gate (point de contrôle)?"
                            >
                              <HelpCircle className="w-3.5 h-3.5" />
                              Guide
                            </button>
                          </div>
                        }
                        bullets={[
                          'Gate 1 (point de contrôle) — Humain / Process / Tech: chaque sujet doit être analysé sur les 3 angles (pas juste technologique).',
                          'Gate 2 (point de contrôle) — Décisionnable: chaque point doit produire un résultat (décision ou action). Sinon: report/parking lot.',
                          'Gate 3 (point de contrôle) — NIST: chaque initiative se rattache à Gouverner, Identifier, Protéger, Détecter, Répondre ou Reprendre.',
                          'Gate 4 (point de contrôle) — Portée: BZ interne vs clients vs veille (on nomme la portée pour éviter de mélanger les objectifs).',
                          'Gate 5 (point de contrôle) — Preuve: décisions et exceptions doivent être documentées (raison, risque, conditions, date de révision, propriétaire).',
                        ]}
                      />

                      <InfoCard
                        title="Ce que ce comité ne doit jamais devenir"
                        icon={<AlertCircle className="w-5 h-5" />}
                        bullets={[
                          'Un comité “PowerPoint” sans décisions, sans actions et sans suivi.',
                          'Un comité d’opinions: les standards et les preuves doivent gagner.',
                          'Un comité qui bloque par réflexe: il doit protéger, mais aussi permettre une exécution saine et efficace.',
                          'Un comité qui mélange tout: sécurité, ops TI, clients et veille doivent être structurés, pas confondus.',
                        ]}
                      />
                    </motion.div>
                  )}

                  {activeTab === 'cadre' && (
                    <motion.div key="cadre" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
                      <CadreHolistiqueTab />
                    </motion.div>
                  )}

                  {activeTab === 'maturite' && (
                    <motion.div key="maturite" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
                      <MaturiteSpiderTab />
                    </motion.div>
                  )}

                  {activeTab === 'raci' && (
                    <motion.div key="raci" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
                      <RolesResponsabilitesTab />
                    </motion.div>
                  )}

                  {activeTab === 'roadmap' && (
                    <motion.div key="roadmap" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} className="space-y-6">
                      <SectionTitle
                        title="Roadmap"
                        subtitle="Première consolidation des sujets/initiatives (à transformer en billets annuels dans le projet Comité Sécurité)."
                      />

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <InfoCard
                          title="Protéger (exemples déjà identifiés)"
                          icon={<ShieldCheck className="w-5 h-5" />}
                          bullets={[
                            'TA Nextcloud pour remplacer FTP.',
                            'TA Déploiement des VLANs de management.',
                            'TS Faille Tunnelvision.',
                            'TA Veeam et SQL.',
                            'TA Tests Action1.',
                            'TS Solutions de backups pour PowerApps.',
                            'TS Tests FIDO2.',
                            'TS Tests Passkey.',
                            'TS Microsoft Purview.',
                            'TS SASE/SSE.',
                          ]}
                        />
                        <InfoCard
                          title="Répondre (clients)"
                          icon={<ClipboardList className="w-5 h-5" />}
                          bullets={[
                            'PD Mise à jour des procédures en cas d’incidents de cybersécurité chez nos clients.',
                            'Standardiser le post-incident : actions, preuves, communication, leçons apprises.',
                          ]}
                        />
                      </div>

                      <InfoCard
                        title="Parc informatique / ops TI / support (à lancer)"
                        icon={<Wrench className="w-5 h-5" />}
                        bullets={[
                          'Baseline poste de travail BZ (config standard + durcissement + conformité).',
                          'Gestion de patchs & vulnérabilités (cadence, couverture, exceptions documentées).',
                          'Process support : priorités, SLA internes, escalade, qualité (réouvertures, satisfaction).',
                          'Cycle de vie : onboarding poste, remplacement, retrait, effacement sécurisé, inventaire.',
                          'Contrôles d’accès : MFA, passkeys/FIDO2 (lien direct avec posture sécurité).',
                        ]}
                      />
                    </motion.div>
                  )}

                  {activeTab === 'rituels' && (
                    <motion.div key="rituels" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} className="space-y-6">
                      <SectionTitle
                        title="Gouvernance & rituels"
                        subtitle="On formalise ce que vous faites déjà bien, et on le rend mesurable et “vivant”."
                      />

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <InfoCard
                          title="Cadence & règles"
                          icon={<Calendar className="w-5 h-5" />}
                          bullets={[
                            'Rencontre mensuelle avec ordre du jour précis.',
                            'Respect du temps (réunion + chaque point).',
                            'Maximum 3 sujets de discussion par rencontre.',
                            'Limiter l’usage d’ordinateur/cell durant les réunions (sauf personne qui a la parole).',
                          ]}
                        />
                        <InfoCard
                          title="Exemple d’ordre du jour (90 minutes)"
                          icon={<ClipboardList className="w-5 h-5" />}
                          bullets={[
                            'Mot de bienvenue + points dernière réunion (5 min).',
                            'Sujet 1 (30 min).',
                            'Sujet 2 (30 min).',
                            'Sujet 3 (15 min).',
                            'Actions : revue, planification, communications (10 min).',
                            'Tour de table : Cyber, TI, Cloud, Ventes (25 min).',
                            'Planifier date prochaine réunion.',
                          ]}
                        />
                      </div>

                      <InfoCard
                        title="Traçabilité (ce qui rend PRIsME supérieur à Word/Excel)"
                        icon={<CheckCircle2 className="w-5 h-5" />}
                        bullets={[
                          'Projet annuel “Comité Sécurité” : phases = sujets; billets = actions; responsable + objectifs SMART.',
                          'Registre des décisions : raison, risque, conditions, preuves, date de révision, propriétaire.',
                          'Communication trimestrielle : synthèse priorités + décisions majeures + ce qui change pour l’équipe.',
                          'Règles de preuve : captures Entra permissions, clauses DPA, rapports SOC2, procédures internes, logs/exports si applicable.',
                        ]}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>

          {/* Mini guide: Gate (point de contrôle) */}
          <AnimatePresence>
            {isGateGuideOpen && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setIsGateGuideOpen(false)}
                  className="fixed inset-0 bg-black/70 backdrop-blur-sm z-60"
                />
                <motion.div
                  initial={{ opacity: 0, scale: 0.98, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.98, y: 10 }}
                  className="fixed inset-0 z-61 flex items-center justify-center p-4"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="w-full max-w-2xl rounded-2xl border border-emerald-500/30 bg-slate-950/90 shadow-2xl shadow-emerald-500/10">
                    <div className="flex items-start justify-between gap-3 p-4 border-b border-emerald-500/20">
                      <div>
                        <p className="text-lg font-bold text-white">Guide — c’est quoi un Gate (point de contrôle)?</p>
                        <p className="text-sm text-gray-200 mt-1">
                          Un <strong className="text-white">Gate (point de contrôle)</strong> = un <strong className="text-white">filtre obligatoire</strong> que chaque sujet doit passer
                          avant qu’on décide. Ça évite les discussions floues et ça force une décision claire et défendable.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setIsGateGuideOpen(false)}
                        className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                        aria-label="Fermer"
                      >
                        <X className="w-5 h-5 text-gray-300" />
                      </button>
                    </div>

                    <div className="p-4 space-y-4 text-sm text-gray-200">
                      <div className="rounded-xl bg-white/5 border border-emerald-500/15 p-4">
                        <p className="font-semibold text-white mb-2">Exemple (Superhuman / nouvel outil)</p>
                        <ul className="list-disc ml-5 space-y-1.5">
                          <li><strong className="text-white">Gate 1 (point de contrôle)</strong> : impacts Humain/Process/Tech (ex: adoption, procédures, permissions, logs).</li>
                          <li><strong className="text-white">Gate 2 (point de contrôle)</strong> : est-ce décisionnable aujourd’hui? sinon “parking lot” + info à obtenir.</li>
                          <li><strong className="text-white">Gate 3 (point de contrôle)</strong> : lien NIST (ex: Protéger/Identifier) et ce que ça améliore réellement.</li>
                          <li><strong className="text-white">Gate 4 (point de contrôle)</strong> : portée (BZ interne vs client vs veille) pour éviter de mélanger les objectifs.</li>
                          <li><strong className="text-white">Gate 5 (point de contrôle)</strong> : preuve et traçabilité (raison, risque, conditions, date de révision, propriétaire).</li>
                        </ul>
                      </div>

                      <div className="rounded-xl bg-white/5 border border-emerald-500/15 p-4">
                        <p className="font-semibold text-white mb-2">Ce que ça vous donne</p>
                        <ul className="list-disc ml-5 space-y-1.5">
                          <li>Décisions plus rapides (on coupe le flou).</li>
                          <li>Moins d’improvisation (mêmes filtres, à chaque fois).</li>
                          <li>Plus défendable (preuve + trace + date de révision).</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </>
      )}
    </AnimatePresence>
  );
}

